/**
 * 병종 트리.
 *
 * 마운트앤블레이드식 진급 사다리다. 문화마다 같은 골격을 쓰되 가지별로 색이 다르다.
 * 골격을 공유하는 이유는 밸런스 때문이다. 문화마다 트리 모양이 다르면
 * 비교할 기준이 사라져서 수치를 잡을 수 없다.
 *
 * 이 파일이 병종 수치의 유일한 출처다. 시뮬레이션도 문서 페이지도 여기서 읽는다.
 */
import {
  CANYON_POWER_PCT,
  CAVALRY_EXTRA_PCT,
  CAVALRY_POWER_PCT,
  HORDE_LEVEL_DROP,
  ELITE_LEVEL_UP,
  HORDE_POWER_PCT,
  LULL_POWER_PCT,
  SIEGE_POWER_PCT,
  SURGE_POWER_PCT,
  Trait,
  WARLORDS,
  hasTrait,
} from './traits.ts';
import { TICK_HZ } from './units.ts';

export const Culture = {
  Goguryeo: 0,
  Baekje: 1,
  Silla: 2,
} as const;
export type Culture = (typeof Culture)[keyof typeof Culture];

export const CULTURE_NAMES: Record<Culture, string> = {
  [Culture.Goguryeo]: '고구려',
  [Culture.Baekje]: '백제',
  [Culture.Silla]: '신라',
};

/** 진급 가지. 티어가 올라도 가지는 바뀌지 않는다(기병 전환만 예외). */
export const Branch = {
  Levy: 'levy',
  Spear: 'spear',
  Blade: 'blade',
  Bow: 'bow',
  Horse: 'horse',
} as const;
export type Branch = (typeof Branch)[keyof typeof Branch];

export const BRANCH_NAMES: Record<Branch, string> = {
  [Branch.Levy]: '민병',
  [Branch.Spear]: '창',
  [Branch.Blade]: '도',
  [Branch.Bow]: '궁',
  [Branch.Horse]: '기',
};

/**
 * 무기. **사거리는 병종이 아니라 무기가 정한다**(사용자: "병종에 공격거리가 있는게 아니라 무기에 있어야해. 병종이 약간의
 * 보너스를 줄수도 있고"). 병종은 무기 하나를 들고, 등급이 `baseTier`를 넘는 만큼 `perTier`씩 더 닿는다 — 숙련의 몫이다.
 * 숙련이 얼마나 늘리는지도 무기가 정한다: 창·칼은 한 걸음, 활은 시위를 끝까지 당기는 힘이라 크다. 공격 동작(무기 × 무술)도
 * 무기를 따른다.
 *
 * 병종 수치에서 옮길 때 모든 병사의 사거리가 한 픽셀도 바뀌지 않게 맞췄다. 나라·상징 병종의 사거리 보정은 무기가 됐다:
 * 고구려 활 +18 → 맥궁, 신라 창 +3 → 장창, 장창당 +4 → 초장창, 노수 +15 → 쇠뇌.
 */
export const Weapon = {
  ShortSpear: 'short_spear',
  Spear: 'spear',
  LongSpear: 'long_spear',
  Pike: 'pike',
  Sword: 'sword',
  Axe: 'axe',
  Farm: 'farm',
  Lance: 'lance',
  HuntBow: 'hunt_bow',
  Bow: 'bow',
  Maekgung: 'maekgung',
  Crossbow: 'crossbow',
} as const;
export type Weapon = (typeof Weapon)[keyof typeof Weapon];

/** 무기의 공격 성격: 한 번의 힘, 공격 간격(틱), 관통. 그 등급 병사가 그 무기를 쓸 때의 값이다. */
export interface AttackProfile {
  readonly atk: number;
  readonly cd: number;
  readonly pierce: number;
}

export interface WeaponDef {
  readonly name: string;
  /** 무기의 성격 한 줄(능력치 카드에 나온다). */
  readonly note: string;
  /** `baseTier`에서 닿는 거리(픽셀). */
  readonly reach: number;
  readonly baseTier: number;
  /** 등급 하나마다 더 닿는 거리. */
  readonly perTier: number;
  /**
   * 등급마다의 공격 성격. 숙련이 무기를 얼마나 잘 쓰게 하는지도 무기가 정한다 — 칼은 등급마다 빨라지고 관통이 늘고,
   * 활은 시위를 당기는 간격이 줄어든다. 병종은 여기에 나라·상징 보정(`CULTURE_DELTA`·설계도 `tweak`)만 얹는다.
   */
  readonly attack: Readonly<Partial<Record<number, AttackProfile>>>;
}

// 공격 성격 표의 줄임: [공격, 간격(틱), 관통].
const A = (atk: number, cd: number, pierce: number): AttackProfile => ({ atk, cd, pierce });

// 창 계열(창·장창·초장창)은 같은 손놀림이되 길수록 되돌려 다시 내지르는 데 시간이 든다(`slower`틱). 신라 창이 더 센 것은
// 나라 보정(`CULTURE_DELTA`)이다.
const SPEAR_HANDS = { 2: A(10, 30, 0), 3: A(13, 29, 0), 4: A(17, 28, 1) };
const slower = (hands: Partial<Record<number, AttackProfile>>, ticks: number): Partial<Record<number, AttackProfile>> =>
  Object.fromEntries(Object.entries(hands).map(([t, a]) => [t, A(a!.atk, a!.cd + ticks, a!.pierce)]));
const BOW_HANDS = { 2: A(8, 44, 0), 3: A(11, 40, 0), 4: A(15, 36, 0) };

/**
 * 병종 수치에서 옮길 때 도끼 하나만 빼고 모든 병사의 공격이 그대로 남게 맞췄다(사용자: "무기마다 공격 성격까지
 * 옮겨줘. 무기 특성에 맞게").
 * - **도끼**(부월수): 칼과 같던 9·24틱 → 11·28틱. 느리고 한 방이 무겁다 — 빼기형 방어라 무거운 한 방이 갑옷에 덜 깎여
 *   갑옷 입은 적에게 칼보다 낫다. 부월수 설명("한 방을 노린다")이 말하던 성격이다. 전투력 지표(`computePower`의 초당 피해)는
 *   1% 안쪽으로 같다. 관통 4도 줘 봤는데 한 등급 위 칼(환도수)과 같아져 "등급이 오를수록 관통이 커진다"가 깨졌다.
 * - **장창·초장창**(신라 창·장창당): 창보다 간격 +1틱·+2틱. 먼저 닿는 대신 되돌리는 데 시간이 든다.
 * - **쇠뇌**(노수)는 그대로다. 관통을 줘 봤다가 되돌렸다: 화살은 이미 방어를 절반만 받아 관통까지 주면 이중이다(tests/combat
 *   "관통은 도검 가지의 것이다"). 쇠뇌의 "철갑을 꿰뚫는다"는 한 발의 무게(20, 활의 1.3배)와 느린 간격이 맡는다 — 빼기형
 *   방어에서는 한 발이 무거울수록 갑옷에 덜 깎인다.
 */
export const WEAPONS: Readonly<Record<Weapon, WeaponDef>> = Object.freeze({
  [Weapon.ShortSpear]: { name: '단창', note: '짧은 창 한 자루. 싸고 가볍다.', reach: 13, baseTier: 1, perTier: 1, attack: { 1: A(7, 26, 0) } },
  [Weapon.Spear]: { name: '창', note: '멀리 닿지만 한 번 내지르는 데 시간이 든다.', reach: 19, baseTier: 2, perTier: 1, attack: SPEAR_HANDS },
  [Weapon.LongSpear]: { name: '장창', note: '창보다 길어 먼저 닿지만 조금 느리다.', reach: 22, baseTier: 2, perTier: 1, attack: slower(SPEAR_HANDS, 1) },
  [Weapon.Pike]: { name: '초장창', note: '밀집 대열로 세우는 긴 창. 가장 먼저 닿지만 가장 느리다.', reach: 28, baseTier: 4, perTier: 1, attack: slower({ 4: SPEAR_HANDS[4] }, 2) },
  [Weapon.Sword]: {
    name: '칼', note: '빠르게 베고 갑옷 틈을 찌른다.', reach: 13, baseTier: 2, perTier: 1,
    attack: { 1: A(9, 25, 2), 2: A(9, 24, 3), 3: A(12, 22, 4), 4: A(16, 21, 5) },
  },
  // 3·4등급은 병사가 들지 않는다 — 영웅이 도끼(철부)를 들 때 견줄 자리다(`HERO_TIER`). 2등급의 칼 대비 성격(공격 ×1.22,
  // 간격 ×1.17, 관통 같음)을 그대로 이었다.
  [Weapon.Axe]: {
    name: '도끼', note: '느리지만 한 방이 무거워 갑옷에 덜 깎인다.', reach: 13, baseTier: 2, perTier: 1,
    attack: { 2: A(11, 28, 3), 3: A(15, 26, 4), 4: A(20, 25, 5) },
  },
  [Weapon.Farm]: { name: '농기구', note: '낫과 쇠스랑. 무기라 하기 어렵다.', reach: 13, baseTier: 1, perTier: 1, attack: { 1: A(6, 27, 0) } },
  [Weapon.Lance]: { name: '기창', note: '말 위에서 내지르는 창. 돌격에 무게가 실린다.', reach: 15, baseTier: 3, perTier: 1, attack: { 3: A(13, 28, 0), 4: A(17, 26, 1) } },
  [Weapon.HuntBow]: { name: '사냥활', note: '짐승 잡던 활. 멀리 가지 못한다.', reach: 210, baseTier: 1, perTier: 35, attack: { 1: A(7, 48, 0) } },
  [Weapon.Bow]: { name: '활', note: '멀리서 먼저 친다. 시위를 당기는 사이가 길다.', reach: 250, baseTier: 2, perTier: 35, attack: BOW_HANDS },
  [Weapon.Maekgung]: { name: '맥궁', note: '고구려의 각궁. 활보다 멀리 나간다.', reach: 268, baseTier: 2, perTier: 35, attack: BOW_HANDS },
  [Weapon.Crossbow]: { name: '쇠뇌', note: '느리지만 한 발이 무거워 철갑에 덜 깎인다.', reach: 335, baseTier: 4, perTier: 35, attack: { 4: A(20, 48, 0) } },
});

/**
 * 쏘는 무기인가(활·쇠뇌). 병사는 가지(`Branch.Bow`)가 말하지만 장수·부하장수는 무기 아이템이 정한다 — 걷는 영웅이 쏘는
 * 무기를 들면 궁수의 가지가 된다(`scenario.mountUp`).
 */
export function isRanged(w: Weapon): boolean {
  return w === Weapon.HuntBow || w === Weapon.Bow || w === Weapon.Maekgung || w === Weapon.Crossbow;
}

/** 그 무기를 든 그 등급의 공격 성격. 표에 없는 등급이면 무기와 병종이 어긋난 것이다(시험이 막는다). */
export function weaponAttack(w: Weapon, tier: number): AttackProfile {
  const a = WEAPONS[w].attack[tier];
  if (!a) throw new Error(`${WEAPONS[w].name}에 ${tier}등급 공격 성격이 없습니다`);
  return a;
}

/** 그 무기를 든 그 등급 병사의 사거리. `baseTier` 아래로는 줄지 않는다(도적 산적은 1등급 칼잡이다). */
export function weaponRange(w: Weapon, tier: number): number {
  const d = WEAPONS[w];
  return d.reach + d.perTier * Math.max(0, tier - d.baseTier);
}

/**
 * 장수·동료가 무기 아이템 없이 드는 것. 무기 아이템을 들면 사거리가 그 무기로 바뀐다 — 영웅 고유의 몫(`LEADER_BASE.range`
 * 등이 칼보다 긴 만큼)은 그대로 얹힌다(`applyLoadout`).
 */
export const HERO_WEAPON: Weapon = Weapon.Sword;

/**
 * 영웅이 무기를 다루는 등급. 영웅의 무기 성격은 이 등급의 표로 견준다 — 4등급 정예만큼 무기를 안다.
 */
export const HERO_TIER = 4;

/** 반올림한 정수 나눗셈(정수만 쓴다 — 시뮬레이션 수치는 정수다). */
function roundDiv(n: number, d: number): number {
  return Math.floor((2 * n + d) / (2 * d));
}

/**
 * **무기를 바꿔 든다** — 병사와 영웅이 함께 쓰는 하나의 규칙(사용자: "장수 동료도 모두 무기 표를 따르도록 해줘. 공통
 * 로직이야"). 같은 등급에서 두 무기의 성격 차이를 무기 표(`WEAPONS`)에서 읽어 옮긴다:
 * - 사거리·관통은 차이만큼 더한다(관통은 0 아래로 내려가지 않는다).
 * - 공격·간격은 **비율**로 옮긴다. 영웅의 간격은 16틱, 병사는 21~30틱이다 — 틱 수 차이를 그대로 옮기면 창을 든 장수가
 *   칼보다 44% 느려져, 병사의 창(칼보다 33% 느림)보다 성격이 부풀었다.
 * 병사는 칼을 든 몸에서 제 무기로 바꿔 든다(`build`). 칼을 든 몸의 수치가 곧 칼의 표 값이라 결과는 무기 표 값과 정확히 같다.
 * 영웅은 칼을 든 몸(`LEADER_BASE` 등)에서 무기 아이템의 종류로 바꿔 든다(`applyLoadout`).
 */
export function rearm<T extends Stats>(s: T, from: Weapon, to: Weapon, tier: number): T {
  if (from === to) return { ...s };
  const a = weaponAttack(from, tier);
  const b = weaponAttack(to, tier);
  return {
    ...s,
    range: s.range + weaponRange(to, tier) - weaponRange(from, tier),
    atk: roundDiv(s.atk * b.atk, a.atk),
    cd: roundDiv(s.cd * b.cd, a.cd),
    pierce: Math.max(0, s.pierce + b.pierce - a.pierce),
  };
}

/** 칼을 든 몸. 병사도 여기서 제 무기로 바꿔 든다(`rearm`). */
function heldSword(body: Omit<Stats, WeaponKeys>, tier: number): Stats {
  return { ...body, range: weaponRange(Weapon.Sword, tier), ...weaponAttack(Weapon.Sword, tier) };
}

/**
 * 영웅(장수·적장·동료·도적 두령)의 맨몸 사거리는 드는 칼의 사거리 + 고유 몫이다. 숫자로 적어 두면 칼의 사거리를 고칠 때
 * 영웅의 맨몸 사거리만 그대로 남고 무기 아이템의 차이(`applyLoadout`)만 바뀐다(리뷰가 짚었다).
 */
const HERO_REACH = WEAPONS[HERO_WEAPON].reach;

/**
 * 걸음 배율(백분율). 표의 `speed`(초당 픽셀)에 곱한 것이 전장의 실제 걸음이다(`World.spawn`). 병사·장수·동료·적 모두에
 * 같이 걸린다 — 병종 사이의 빠르기 비는 표 그대로다.
 *
 * 까닭(밸런스 기준, 2026-10-02): 양군이 출발해 맞붙기까지 4~6초여서 배치와 대형을 보고 명령을 내릴 틈이 없었고, 보통
 * 판이 16~27초에 끝났다. 전장 세로를 늘리는 것(`FIELD_ROWS`)과 함께 걸음을 15% 낮춰 맞붙기 전을 10~12초로 만든다.
 * 걸음만 70%까지 낮춰도 판은 4~7초밖에 늘지 않았다(`npm run length`) — 길이를 정하는 것은 거리다. 걸음은 "달려가
 * 부딪친다"는 느낌을 누그러뜨리는 몫이다.
 *
 * 표의 수치를 하나하나 깎지 않은 것은 전투력 식의 기동 몫(`troopPower`)과 장비의 걸음 보정이 표의 값을 기준으로 서
 * 있어서다. 화면에 적는 걸음은 `pacedSpeed`를 거친다.
 */
export const PACE_PCT = 85;

/**
 * 전장에 선 **병사**의 체력 배율(백분율). 표의 체력은 그대로 두고 전장에 세울 때 한 번 곱한다(`World.spawn`) — 걸음
 * (`PACE_PCT`)과 같은 까닭이다: 전투력 식과 적 머릿수 셈이 표의 값을 기준으로 서 있다. 양쪽 병사 모두에 걸린다. 장수·
 * 동료·적장은 그대로다(착장과 레벨의 몫이다).
 *
 * 플레이한 사람이 정했다(Todo11): "예전에 전투 시간 좀 늘린다고 진영 사이 거리를 벌렸는데 이건 취소하자. 이동 시간이
 * 기니까 지루하다. 대신 모든 병사의 HP를 20프로 증가시키자. 싸우는 모습을 좀 볼 수 있게." 판의 길이를 걷는 시간이
 * 아니라 싸우는 시간으로 낸다(`FIELD_ROWS`).
 *
 * **120 → 138**(2026-10-07, `REPLAY_VERSION` 110). 기본 명령이 공격이 된 뒤(108) 판이 짧아졌다 — 손 안 댄 판의 평균이
 * 25 → 17초(idb-test). 플레이한 사람: "전투가 조금 길었으면 좋겠는데 모든 캐릭터들의 체력을 15프로정도 증가시키는 안에
 * 대해 어떻게 생각해?" 내가 병사만 올리기를 권했고(장수·부하장수·적장까지 올리면 장수가 쓰러져 끝나는 판이 줄어 실패의
 * 무게가 가벼워진다) 그렇게 정했다. 값은 말한 15%를 그대로 곱한 것이다(120 × 1.15). 체력은 수가 많은 쪽에 더 유리하다 —
 * 수가 적은 쪽이 불리한 첫 관문과 깊은 관문은 따로 봐야 한다.
 */
export const SOLDIER_HP_PCT = 138;

/** 전장에서의 실제 병사 체력(버림). 시뮬레이션(`World.spawn`)과 화면이 같이 쓴다. */
export function fieldHp(hp: number): number {
  return Math.floor((hp * SOLDIER_HP_PCT) / 100);
}

/** 전장에서의 실제 걸음(초당 픽셀, 버림). 화면과 검산 도구가 쓴다 — 시뮬레이션은 `World.spawn`에서 따로 곱한다. */
export function pacedSpeed(speed: number): number {
  return Math.floor((speed * PACE_PCT) / 100);
}

/** 전투 수치. 전부 정수이며 시뮬레이션이 그대로 쓴다. */
export interface Stats {
  hp: number;
  atk: number;
  /** 고정 차감형 방어. 약한 공격일수록 비율로 크게 막는다. */
  def: number;
  /** 사거리(픽셀). */
  range: number;
  /** 초당 픽셀 이동 속도. 전장에서는 걸음 배율(`PACE_PCT`)이 곱해진다. */
  speed: number;
  /** 공격 간격(틱). 30틱이 1초다. */
  cd: number;
  radius: number;
  mass: number;
  /** 사기 저항. 높을수록 늦게 무너진다. */
  grit: number;
  /**
   * 관통. 상대 방어를 이만큼 깎고 때린다.
   *
   * 방어가 고정 차감이라 약한 공격은 거의 전부 막히고 강한 공격은 방어를 거의
   * 무시한다. 민병(공격 7)이 개마무사(방어 6)를 119대 때려야 죽이는 것이 그
   * 결과다. 명중률 같은 곱셈 보정을 더해도 이 비는 그대로다. 양쪽에 똑같이
   * 곱해지기 때문이다. 관통은 그 뺄셈을 직접 건드린다.
   *
   * 도검 가지의 정체성이기도 하다. 상성표에서 도검은 활에만 강한 밋밋한
   * 가지였다. 도끼와 둔기가 갑옷을 깨는 쪽을 맡는다.
   */
  pierce: number;
}

export interface TroopDef extends Stats {
  /** 드는 무기. 사거리는 여기서 나온다(`weaponRange`). */
  weapon: Weapon;
  id: string;
  name: string;
  culture: Culture;
  /**
   * 적만 쓰는 병종. 플레이어는 모집도 진급도 할 수 없다.
   *
   * 도적처럼 나라에 속하지 않은 무리다. 문화 자리에 값이 들어가 있는 것은
   * `TroopDef`가 그걸 요구하기 때문이고, 실제로 그 나라 소속이라는 뜻이 아니다.
   * 편성·상점·검산 표는 `PLAYER_TROOPS`를 쓴다. 모집이 `${slug}_levy1`을
   * 직접 부르고 진급이 `upgradesTo`만 타기 때문에 표를 훑는 곳이 애초에
   * 적지만, 훑는 곳이 생기면 그쪽도 `PLAYER_TROOPS`를 봐야 한다. 스프라이트를
   * 굽는 `atlas.ts`만 도적까지 필요해서 `TROOPS` 전체를 본다.
   */
  enemyOnly?: boolean;
  branch: Branch;
  tier: number;
  /** 문화를 상징하는 정점 병종. 문화당 하나다. */
  signature: boolean;
  /** 진급 가능한 상위 병종. */
  upgradesTo: string[];
  /** 이 병종으로 올리는 데 드는 돈. */
  upgradeCost: number;
  /**
   * 진급에 필요한 레벨. 올릴 곳이 없으면 0이다.
   *
   * 예전에는 누적 경험치였고 진급하면 경험치가 0으로 돌아갔다. 병사가 레벨을 갖게
   * 되면서 경험치는 레벨의 출처가 됐다 — 진급으로 지우면 레벨도 같이 사라진다.
   * 그래서 문턱을 레벨로 바꾸고 경험치는 그대로 둔다.
   */
  levelToUpgrade: number;
  note: string;
}

/** 무기가 정하는 수치(`WEAPONS`): 사거리와 공격 성격. 병종 뼈대에는 없다. */
type WeaponKeys = 'range' | 'atk' | 'cd' | 'pierce';

/** 가지와 티어별 몸의 수치(체력·방어·이동·크기·사기). 문화 보정 전의 뼈대다. 사거리·공격 성격은 무기가 정한다(`WEAPONS`). */
const BASE: Record<string, Omit<Stats, WeaponKeys>> = {
  'levy:1': { hp: 34, def: 0, speed: 70, radius: 5, mass: 3, grit: 80 },

  'spear:2': { hp: 46, def: 2, speed: 66, radius: 5, mass: 4, grit: 120 },
  'spear:3': { hp: 64, def: 4, speed: 64, radius: 5, mass: 5, grit: 150 },
  'spear:4': { hp: 88, def: 6, speed: 63, radius: 6, mass: 6, grit: 180 },

  'blade:2': { hp: 44, def: 1, speed: 74, radius: 5, mass: 3, grit: 110 },
  'blade:3': { hp: 62, def: 3, speed: 76, radius: 5, mass: 4, grit: 145 },
  'blade:4': { hp: 86, def: 5, speed: 78, radius: 6, mass: 5, grit: 175 },

  'bow:2': { hp: 30, def: 0, speed: 64, radius: 5, mass: 2, grit: 75 },
  'bow:3': { hp: 40, def: 1, speed: 66, radius: 5, mass: 3, grit: 100 },
  'bow:4': { hp: 52, def: 2, speed: 68, radius: 5, mass: 3, grit: 125 },

  'horse:3': { hp: 84, def: 3, speed: 126, radius: 8, mass: 7, grit: 140 },
  'horse:4': { hp: 104, def: 6, speed: 132, radius: 8, mass: 8, grit: 170 },
};

/** 문화 보정. 가지별로 더하거나 곱한다. 합이 0인 가지는 기본값 그대로다. */
type Delta = Partial<Record<keyof Stats, number>>;
const CULTURE_DELTA: Record<Culture, Partial<Record<Branch, Delta>>> = {
  // 고구려는 말이다. 개마무사로 끝나는 기병 가지가 가장 두껍고, 맥궁이 멀리 나간다(무기 `CULTURE_WEAPON`).
  [Culture.Goguryeo]: {
    [Branch.Horse]: { hp: 8, atk: 1, grit: 10 },
    [Branch.Blade]: { atk: 1, def: -1 },
  },
  // 백제는 활과 철갑이다. 원거리가 가장 아프고 보병이 잘 버티되 말이 약하다.
  [Culture.Baekje]: {
    [Branch.Bow]: { atk: 2, cd: -3 },
    [Branch.Blade]: { def: 1, hp: 4 },
    [Branch.Spear]: { def: 1 },
    [Branch.Horse]: { hp: -8, speed: -4 },
  },
  // 신라는 장창과 밀집이다. 창이 길고(무기 `CULTURE_WEAPON`) 병사가 늦게 무너진다.
  [Culture.Silla]: {
    [Branch.Spear]: { atk: 1, mass: 1 },
    [Branch.Blade]: { grit: 20 },
    [Branch.Bow]: { atk: -1 },
  },
};

/** 가지마다 드는 무기. 나라가 바꾸면 `CULTURE_WEAPON`, 한 병종만 다르면 설계도의 `weapon`. */
const BRANCH_WEAPON: Record<Branch, Weapon> = {
  [Branch.Levy]: Weapon.ShortSpear,
  [Branch.Spear]: Weapon.Spear,
  [Branch.Blade]: Weapon.Sword,
  [Branch.Bow]: Weapon.Bow,
  [Branch.Horse]: Weapon.Lance,
};

const CULTURE_WEAPON: Record<Culture, Partial<Record<Branch, Weapon>>> = {
  [Culture.Goguryeo]: { [Branch.Bow]: Weapon.Maekgung },
  [Culture.Baekje]: {},
  [Culture.Silla]: { [Branch.Spear]: Weapon.LongSpear },
};

/** hp와 speed 보정은 백분율, 나머지는 덧셈이다. */
const PERCENT_KEYS = new Set<keyof Stats>(['hp', 'speed']);

function applyDelta(base: Stats, delta: Delta | undefined): Stats {
  const out: Stats = { ...base };
  if (!delta) return out;
  for (const k of Object.keys(delta) as (keyof Stats)[]) {
    const v = delta[k];
    if (v === undefined) continue;
    out[k] = PERCENT_KEYS.has(k) ? Math.round(base[k] * (1 + v / 100)) : base[k] + v;
  }
  return out;
}

/** 진급 비용. 기병은 말값이 붙는다. */
const TIER_COST: Record<number, number> = { 1: 0, 2: 40, 3: 80, 4: 240 };
/**
 * 이 티어에서 다음 티어로 오르는 레벨. 4티어는 더 오를 곳이 없다.
 *
 * 레벨은 옛 눈금 3·5·8을 새 눈금(레벨 99)으로 옮긴 4·8·13이고(`levelFromOld`), 문턱 경험치는 100/436/1180이다
 * (`levelXp` — 옛 60/200/560의 두 배 남짓). 생환 경험치가 깊이 1에서 24, 한 깊이마다 +4라(`battleXp` — 밸런스 2에서
 * 두 배로 올렸다, 그 전에는 12와 +2), 깊이 1만 돌면 민병은 다섯 판 남짓이다(그 전에는 아홉 판). 처음에 "민병은 두 판"이라 적었다가 셈이 틀렸다고 리뷰가 짚은 자리다 — 문턱을
 * 바꾸면 여기 셈도 다시 한다.
 */
const TIER_LEVEL: Record<number, number> = { 1: 4, 2: 8, 3: 13, 4: 0 };
const HORSE_COST_PCT = 150;

// ---------------------------------------------------------------------------
// 레벨
// ---------------------------------------------------------------------------

/**
 * 병사와 장수의 레벨 상한.
 *
 * 병사를 작게 가기로 하면서 한 명 한 명이 자라야 했다. 수백 명일 때는 병종 티어가
 * 성장의 전부였지만, 열두 명짜리 분대에서는 "이 사람이 몇 번 살아 돌아왔는가"가
 * 보여야 잃는 것이 무겁다.
 */
export const LEVEL_MAX = 99;

/**
 * **레벨 99, 성장은 두 배 느리게**(규칙 40에서 바뀜). 플레이한 사람이 청했다: "레벨 20이 최고인가 보네. 99를 최고로
 * 해 주고 전체적으로 성장이 지금보다 두 배 느리게 해 줘. 상장군 같은 계급도 마찬가지야." 레벨 99의 강함은 "지금
 * 레벨 20과, 레벨당 몫을 그대로 둔 99의 중간쯤"으로 정했다(절반쯤).
 *
 * - 레벨 하나의 몫은 옛 것의 3/5(`LEVEL_STEP_NUM/DEN`). 옛 레벨 L의 강함은 새 레벨 `levelFromOld(L)`이다 —
 *   옛 20 = 새 33, 새 99 = 옛 눈금의 59.8(옛 몫대로 99까지 가면 체력 +392%, 옛 20은 +76%, 새 99는 +235%).
 * - 같은 강함에 드는 경험치는 두 배다: 새 레벨 n의 경험치 = 2 × 옛 곡선(옛 레벨 1 + (n−1)·3/5).
 * - 쌓인 경험치는 그대로 둔다(플레이한 사람이 정했다) — 옛 계정의 부대는 경험치 절반 몫의 강함이 된다.
 */
export const LEVEL_STEP_NUM = 3;
export const LEVEL_STEP_DEN = 5;

/** 옛 눈금의 레벨 상한. 옛 레벨은 여기서 먼저 잘린다(`levelFromOld`). */
export const OLD_LEVEL_MAX = 20;

/**
 * 옛 눈금(상한 20)의 레벨을 같은 강함의 새 레벨로: 1 + round((L−1)·5/3). 적장·진급 레벨·검산 도구·옛 동료 단계가 쓴다.
 *
 * 옛 레벨을 **먼저 옛 상한에서 자른다.** 적장의 레벨은 깊이 그대로라 깊이 21~30에서 옛 코드는 20에서 막혔는데(`clampLevel`),
 * 자르지 않고 옮겼더니 34~49가 나와 깊이 30 적장 체력이 옛것보다 25% 셌다(리뷰가 짚었다). 반올림은 정수식으로 한다 —
 * 시뮬레이션 코어에 실수 중간값을 두지 않는다(`((L−1)·10 + 3) / 6`의 내림이 1~20 모두 같은 값).
 */
export function levelFromOld(old: number): number {
  const o = Math.max(1, Math.min(OLD_LEVEL_MAX, Math.floor(old)));
  return clampLevel(1 + Math.floor(((o - 1) * 10 + 3) / 6));
}

/**
 * 이 레벨이 되는 누적 경험치. 레벨 1은 0이다. 옛 곡선은 `10·L·(L−1)`이었다. 새 레벨 n은 옛 눈금의
 * m = 1 + 3(n−1)/5이고, 두 배 느리게: 20·m·(m−1) = 4(3n+2)(3n−3)/5.
 */
export function levelXp(level: number): number {
  return Math.floor((4 * (3 * level + 2) * (3 * level - 3)) / 5);
}

/** 누적 경험치의 레벨. 1~LEVEL_MAX. */
export function levelOf(xp: number): number {
  let l = 1;
  while (l < LEVEL_MAX && xp >= levelXp(l + 1)) l++;
  return l;
}

/**
 * 명부·리플레이에서 들어온 레벨을 믿을 수 있는 정수로 만든다.
 *
 * NaN은 `Math.floor`도 `Math.min/max`도 그대로 통과해 체력 배열에 0으로 들어간다.
 * 동료 단계에서 한 번 밟은 함정이라 문 앞에서 막는다.
 */
export function clampLevel(raw: unknown): number {
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return 1;
  return Math.max(1, Math.min(LEVEL_MAX, Math.floor(raw)));
}

/** 병사 레벨 하나당 체력·공격 증가(천분율 ‰). 옛 5%·4%의 3/5. */
export const SOLDIER_LEVEL_HP_PERMILLE = 30;
export const SOLDIER_LEVEL_ATK_PERMILLE = 24;

/**
 * 레벨이 붙은 능력치.
 *
 * 레벨 1에서부터의 **누적** 증가분을 한 번에 내림한다. 레벨마다 내림하며 쌓으면
 * 공격 7짜리 민병은 4%가 늘 0.28로 잘려 스무 레벨이 되어도 7이다. 전부 정수다.
 */
function leveled(s: Stats, level: number, hpPermille: number, atkPermille: number, defEvery = 0): Stats {
  const n = clampLevel(level) - 1;
  // 늘 새 객체를 돌려준다. `LEADER_BASE`는 얼려 둔 공용 상수라 그대로 흘려보내면
  // 받는 쪽이 고치는 순간 터지거나, 얼리지 않은 표라면 조용히 모두가 바뀐다.
  if (n === 0) return { ...s };
  return {
    ...s,
    hp: s.hp + Math.floor((s.hp * hpPermille * n) / 1000),
    atk: s.atk + Math.floor((s.atk * atkPermille * n) / 1000),
    def: defEvery > 0 ? s.def + Math.floor(n / defEvery) : s.def,
  };
}

/** 병사(와 적장)의 레벨 능력치. */
export function soldierStats(s: Stats, level: number): Stats {
  return leveled(s, level, SOLDIER_LEVEL_HP_PERMILLE, SOLDIER_LEVEL_ATK_PERMILLE);
}

/**
 * 레벨이 오를 때 붙는 몫은 병사마다 조금씩 다르다: 체력 2~4%, 공격 1.4~3.4%(평균은 위 3%·2.4% 그대로, 천분율로 굴린다).
 * 플레이한 사람이 "레벨 승급시 올라가는 능력치에 약간의 랜덤을 주자"고 했다.
 *
 * **굴림은 병사 uid와 레벨에서 정해진다.** 계정에 따로 적지 않는다 — 명부에 이미 uid와 레벨이 실리므로
 * 서버가 같은 몸을 다시 만든다. 계정에 적었다면 명부에 몫을 더 실어야 했고, 옛 계정은 굴린 적이 없어
 * 옮길 방법도 필요했다. 같은 병사가 같은 레벨에 오르면 늘 같은 몫이다(다시 굴려 뽑을 수 없다).
 * uid가 없는 병력(적병)은 평균값 그대로다.
 */
export interface Growth {
  /** 레벨 1에서부터 쌓인 체력 증가(천분율 포인트 ‰). */
  readonly hp: number;
  readonly atk: number;
}

/** `level-1 → level`로 오를 때 받은 몫. 정수 해시다(부동소수점·Math.random 없음). */
export function growthRoll(uid: number, level: number): Growth {
  let h = Math.imul((uid | 0) ^ 0x5bd1e995, 0x9e3779b1) ^ Math.imul(level | 0, 0x85ebca6b);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  h = Math.imul(h, 0x297a2d39);
  h ^= h >>> 15;
  const r = h >>> 0;
  return {
    hp: SOLDIER_LEVEL_HP_PERMILLE - 10 + 10 * (r % 3),
    atk: SOLDIER_LEVEL_ATK_PERMILLE - 10 + 10 * (Math.floor(r / 3) % 3),
  };
}

/** 이 병사가 이 레벨까지 쌓은 몫. 레벨 1은 0이다. */
export function soldierGrowth(uid: number, level: number): Growth {
  const lv = clampLevel(level);
  let hp = 0;
  let atk = 0;
  for (let k = 2; k <= lv; k++) {
    const g = growthRoll(uid, k);
    hp += g.hp;
    atk += g.atk;
  }
  return { hp, atk };
}

/** 쌓인 몫을 몸에 얹는다. `leveled`처럼 누적분을 한 번에 내림한다. */
function grown(s: Stats, g: Growth): Stats {
  return {
    ...s,
    hp: s.hp + Math.floor((s.hp * g.hp) / 1000),
    atk: s.atk + Math.floor((s.atk * g.atk) / 1000),
  };
}

interface Blueprint {
  branch: Branch;
  tier: number;
  name: string;
  note: string;
  signature?: boolean;
  /** 기본 가지 규칙을 벗어나는 추가 보정. 상징 병종에만 쓴다. */
  tweak?: Delta;
  /** 가지·나라의 무기와 다른 것을 들면. */
  weapon?: Weapon;
}

/** 문화별 이름표. 골격은 같고 이름과 상징만 다르다. */
const ROSTERS: Record<Culture, Blueprint[]> = {
  [Culture.Goguryeo]: [
    { branch: Branch.Levy, tier: 1, name: '고구려 민병', note: '징발된 농민. 창 한 자루가 전부다.' },
    { branch: Branch.Spear, tier: 2, name: '고구려 창수', note: '기병을 세우는 첫 번째 벽.' },
    { branch: Branch.Blade, tier: 2, name: '부월수', note: '도끼를 쓴다. 방어를 덜 챙기고 한 방을 노린다.', weapon: Weapon.Axe },
    { branch: Branch.Bow, tier: 2, name: '맥궁수', note: '맥궁은 멀리 나간다. 고구려 활의 시작.' },
    { branch: Branch.Spear, tier: 3, name: '숙련 창수', note: '대열을 지킬 줄 안다.' },
    { branch: Branch.Blade, tier: 3, name: '환도수', note: '환두대도를 찬 직업 군인.' },
    { branch: Branch.Bow, tier: 3, name: '맥궁 사수', note: '사거리에서 다른 나라 궁수를 압도한다.' },
    { branch: Branch.Horse, tier: 3, name: '고구려 기병', note: '말 위에서 자란다. 다른 나라 기병보다 단단하다.' },
    { branch: Branch.Spear, tier: 4, name: '철갑 창수', note: '찰갑을 두르고 버틴다.' },
    { branch: Branch.Blade, tier: 4, name: '조의선인', note: '검은 옷의 무사단. 무너지지 않는다.' },
    { branch: Branch.Bow, tier: 4, name: '맥궁 명사수', note: '접근하기 전에 절반이 쓰러진다.' },
    {
      branch: Branch.Horse,
      tier: 4,
      name: '개마무사',
      note: '사람도 말도 철갑을 둘렀다. 부딪히면 대열이 뚫린다.',
      signature: true,
      tweak: { hp: 6, atk: 1, mass: 1 },
    },
  ],
  [Culture.Baekje]: [
    { branch: Branch.Levy, tier: 1, name: '백제 민병', note: '징발된 농민. 방패는 있다.' },
    { branch: Branch.Spear, tier: 2, name: '백제 창수', note: '방패를 함께 든다.' },
    { branch: Branch.Blade, tier: 2, name: '백제 도수', note: '철제 무장이 이른 시기부터 좋았다.' },
    { branch: Branch.Bow, tier: 2, name: '백제 궁수', note: '백제 사람은 활을 잘 쏜다고 기록됐다.' },
    { branch: Branch.Spear, tier: 3, name: '방패 창병', note: '앞을 막고 버티는 쪽에 특화됐다.' },
    { branch: Branch.Blade, tier: 3, name: '백제 정병', note: '철갑을 갖춘 상비군.' },
    { branch: Branch.Bow, tier: 3, name: '담로 궁수', note: '담로에서 뽑아 올린 활잡이.' },
    { branch: Branch.Horse, tier: 3, name: '백제 기병', note: '말은 귀하다. 수가 적고 가볍다.' },
    { branch: Branch.Spear, tier: 4, name: '흑치 창병', note: '전열 한복판을 맡는다.' },
    { branch: Branch.Blade, tier: 4, name: '방령군', note: '오방을 지키는 정예. 좀처럼 뚫리지 않는다.' },
    {
      branch: Branch.Bow,
      tier: 4,
      name: '노수',
      note: '쇠뇌를 쏜다. 느리지만 철갑을 꿰뚫는다.',
      signature: true,
      weapon: Weapon.Crossbow,
    },
    { branch: Branch.Horse, tier: 4, name: '백제 철기', note: '수는 적어도 철갑은 두툼하다.' },
  ],
  [Culture.Silla]: [
    { branch: Branch.Levy, tier: 1, name: '신라 민병', note: '징발된 농민. 대열은 배웠다.' },
    { branch: Branch.Spear, tier: 2, name: '신라 창수', note: '창이 남들보다 길다.' },
    { branch: Branch.Blade, tier: 2, name: '신라 도수', note: '물러설 줄 모른다.' },
    { branch: Branch.Bow, tier: 2, name: '신라 궁수', note: '활은 보조다. 신라의 주력은 창이다.' },
    { branch: Branch.Spear, tier: 3, name: '장창병', note: '긴 창으로 기병을 세운다.' },
    { branch: Branch.Blade, tier: 3, name: '서당 보병', note: '서당에 편성된 상비 보병.' },
    { branch: Branch.Bow, tier: 3, name: '육정 궁수', note: '육정에 딸린 활잡이.' },
    { branch: Branch.Horse, tier: 3, name: '신라 기병', note: '평범하지만 빠지는 것도 없다.' },
    {
      branch: Branch.Spear,
      tier: 4,
      name: '장창당',
      note: '초장창을 세운 밀집 대열. 기병이 들어오면 그대로 멈춘다.',
      signature: true,
      tweak: { def: 1, mass: 1, speed: -4 },
      weapon: Weapon.Pike,
    },
    { branch: Branch.Blade, tier: 4, name: '화랑', note: '주변 병사의 사기를 붙든다.' },
    { branch: Branch.Bow, tier: 4, name: '사자금당 궁수', note: '신라 활의 정점.' },
    { branch: Branch.Horse, tier: 4, name: '신라 철기', note: '균형 잡힌 중기병.' },
  ],
};

const CULTURE_SLUG: Record<Culture, string> = {
  [Culture.Goguryeo]: 'go',
  [Culture.Baekje]: 'bj',
  [Culture.Silla]: 'sl',
};

function troopId(culture: Culture, branch: Branch, tier: number): string {
  return `${CULTURE_SLUG[culture]}_${branch}${tier}`;
}

/**
 * 진급 경로.
 * 민병은 세 가지 중 하나를 고른다. 창과 도는 기병으로 갈아탈 수 있다.
 * 활은 말에 오르지 못한다. 궁기병을 넣으면 상성 삼각형이 무너진다.
 */
function upgradePaths(culture: Culture, branch: Branch, tier: number): string[] {
  const id = (b: Branch, t: number): string => troopId(culture, b, t);
  if (branch === Branch.Levy) return [id(Branch.Spear, 2), id(Branch.Blade, 2), id(Branch.Bow, 2)];
  if (tier >= 4) return [];
  const next: string[] = [id(branch, tier + 1)];
  if (tier === 2 && (branch === Branch.Spear || branch === Branch.Blade)) {
    next.push(id(Branch.Horse, 3));
  }
  return next;
}

function build(): TroopDef[] {
  const out: TroopDef[] = [];
  for (const culture of [Culture.Goguryeo, Culture.Baekje, Culture.Silla] as Culture[]) {
    for (const bp of ROSTERS[culture]) {
      const base = BASE[`${bp.branch}:${bp.tier}`];
      if (!base) throw new Error(`뼈대 수치가 없습니다: ${bp.branch}:${bp.tier}`);
      const weapon = bp.weapon ?? CULTURE_WEAPON[culture][bp.branch] ?? BRANCH_WEAPON[bp.branch];
      let stats = applyDelta(rearm(heldSword(base, bp.tier), Weapon.Sword, weapon, bp.tier), CULTURE_DELTA[culture][bp.branch]);
      stats = applyDelta(stats, bp.tweak);
      const isHorse = bp.branch === Branch.Horse;
      const cost = Math.round((TIER_COST[bp.tier] * (isHorse ? HORSE_COST_PCT : 100)) / 100);
      out.push({
        id: troopId(culture, bp.branch, bp.tier),
        name: bp.name,
        culture,
        branch: bp.branch,
        tier: bp.tier,
        signature: bp.signature === true,
        upgradesTo: upgradePaths(culture, bp.branch, bp.tier),
        upgradeCost: cost,
        levelToUpgrade: TIER_LEVEL[bp.tier],
        note: bp.note,
        weapon,
        ...stats,
      });
    }
  }
  return out;
}

/**
 * 도적. 나라에 속하지 않은 무리다.
 *
 * 있는 이유는 하나다 — **장수 혼자 돈을 벌어올 자리**. 부대를 전부 잃으면 돈이
 * 나올 데가 없는데, 돈은 전투에서만 나오고 전투에는 병사가 필요했다. 도적 토벌은
 * 병사 없이 장수만으로 성립하는 유일한 싸움이다.
 *
 * 그래서 수치가 약하다. 정규군과 붙으라고 만든 것이 아니라, 장수 하나가 열댓을
 * 상대해 이길 수 있어야 한다. 사기가 특히 낮다 — 도적은 불리하면 흩어진다.
 * 대신 값이 싸서 토벌만 반복하는 것이 진짜 출정보다 이득이 되지 않는다.
 */
const BANDITS: { id: string; name: string; branch: Branch; weapon: Weapon; tier?: number; stats: Omit<Stats, WeaponKeys>; note: string }[] = [
  {
    id: 'bandit_thug',
    name: '도적',
    branch: Branch.Levy,
    weapon: Weapon.Farm,
    stats: { hp: 30, def: 0, speed: 72, radius: 5, mass: 3, grit: 45 },
    note: '굶주려 산으로 든 자들. 무기라고는 농기구뿐이다.',
  },
  {
    id: 'bandit_raider',
    name: '산적',
    branch: Branch.Blade,
    weapon: Weapon.Sword,
    stats: { hp: 42, def: 1, speed: 76, radius: 5, mass: 3, grit: 70 },
    note: '노획한 칼을 찬 자. 싸움에 익숙하다.',
  },
  {
    id: 'bandit_archer',
    name: '도적 궁수',
    branch: Branch.Bow,
    weapon: Weapon.HuntBow,
    stats: { hp: 26, def: 0, speed: 66, radius: 5, mass: 2, grit: 40 },
    note: '사냥하던 활을 들었다. 멀리 가지 못한다.',
  },
  // 큰 무리(`RAID_BANDS`)에만 선다. 창을 든 탈영병은 말 탄 장수에게 아프고(창의 상성), 마적은 장수를 쫓아와 들이받는다 —
  // 큰 무리가 졸개의 수만 많은 판이 되지 않게 한다. 사기는 여전히 정규군보다 낮다.
  {
    id: 'bandit_deserter',
    name: '탈영병',
    branch: Branch.Spear,
    weapon: Weapon.Spear,
    tier: 2,
    stats: { hp: 58, def: 2, speed: 70, radius: 5, mass: 4, grit: 90 },
    note: '군영에서 달아나 산채에 든 병사. 창을 다룰 줄 안다 — 말 탄 장수에게 위험하다.',
  },
  {
    id: 'bandit_rider',
    name: '마적',
    branch: Branch.Horse,
    weapon: Weapon.Lance,
    tier: 3,
    stats: { hp: 80, def: 2, speed: 108, radius: 7, mass: 7, grit: 95 },
    note: '말을 훔쳐 탄 도적. 달려와 들이받는다.',
  },
];

function buildBandits(): TroopDef[] {
  return BANDITS.map((b) => ({
    id: b.id,
    name: b.name,
    // 문화 자리를 비울 수 없어 하나를 적어 둔다. 소속이 아니다.
    culture: Culture.Goguryeo,
    branch: b.branch,
    tier: b.tier ?? 1,
    signature: false,
    upgradesTo: [],
    upgradeCost: 0,
    levelToUpgrade: 0,
    enemyOnly: true,
    note: b.note,
    weapon: b.weapon,
    ...rearm(heldSword(b.stats, b.tier ?? 1), Weapon.Sword, b.weapon, b.tier ?? 1),
  }));
}

export const TROOPS: readonly TroopDef[] = [...build(), ...buildBandits()];

/** 플레이어가 가질 수 있는 병종만. 편성 화면과 검산 표가 이것을 쓴다. */
export const PLAYER_TROOPS: readonly TroopDef[] = TROOPS.filter((t) => t.enemyOnly !== true);

/**
 * 도적떼 한 무리. 장수 혼자 상대할 수 있는 크기다.
 *
 * 수를 시드로 흔들지 않는다. 토벌은 도박이 아니라 바닥에서 일어서는 길이라,
 * 나갈 때마다 난이도가 달라지면 그 노릇을 못 한다.
 */
export function banditRoster(band = 0): { troop: string; count: number; level?: number }[] {
  const b = RAID_BANDS[raidBand(band)];
  return b.roster.map((e) => ({ troop: e.troop, count: e.count, ...(b.level > 1 ? { level: b.level } : {}) }));
}

/** 도적 두령의 맨몸. 가장 작은 무리의 두령이다 — 큰 무리의 두령은 레벨이 오른다(`banditChiefSpec`). */
const BANDIT_CHIEF: Stats = {
  hp: 180,
  atk: 14,
  def: 2,
  range: HERO_REACH + 2,
  speed: 104,
  cd: 20,
  radius: 8,
  mass: 8,
  grit: 150,
  pierce: 1,
};

/** 도적 두령. 토벌의 적장 자리다. 정규군 적장보다 한참 약하다. 큰 무리의 두령은 레벨만큼 세다(병사와 같은 성장식). */
export function banditChiefSpec(band = 0): CombatantSpec {
  const b = RAID_BANDS[raidBand(band)];
  const spec = hero('bandit_chief', b.chief, Role.Chief, Culture.Goguryeo, soldierStats(BANDIT_CHIEF, b.chiefLevel));
  return b.chiefLevel > 1 ? { ...spec, level: b.chiefLevel } : spec;
}

/**
 * **도적 무리의 크기**(단신 토벌에서 고른다). 0번은 예전의 하나뿐이던 무리 그대로다 — 부대를 다 잃었을 때 안전하게
 * 일어서는 길이라 한 틱도 바꾸지 않는다. 그 위는 장수의 관등이 오르면 열리고(`meta/army.ts`의 `raidBandsOpen` — 색인이
 * 곧 필요한 관등), 수가 많고 세서 장수가 실제로 쓰러질 수 있으며 현상금이 크다.
 *
 * 플레이한 사람이 청했다(2026-10-09): "혼자 나선다도 나중에는 돈 좀 많이 벌 수 있게 해줘. 장수가 크니까 적이 너무 약하고
 * 돈도 너무 적어." 같이 정한 것: 무리를 **고른다**(자동으로 세지지 않는다 — 장비 없이 레벨만 높은 장수가 바닥에서 못
 * 일어서면 안 된다), 벌이는 같은 성장 단계의 **출정 한 번의 3분의 1쯤**(토벌은 급료도 없고 병사도 걸지 않아 더 후하면
 * 출정 대신 토벌만 돈다), 지면 **잃는 것은 없고 번 돈만 없다**.
 *
 * `level`: 졸개의 레벨(병사의 성장식). `bounty`: 두령을 벴을 때의 현상금 — 졸개를 벤 노획(`battleGold`)에 얹는다.
 * 값은 `npm run raid`로 맞췄다(그 관등의 상점 장비 장수가 이기되 질 수도 있게, 벌이는 목표에).
 *
 * **126: 앞쪽 무리의 현상금을 올렸다**(도적 소굴 300 → 500, 큰 산채 650 → 1,000, 마적단 1,400 → 1,600). 처음의 값은
 * "출정 한 번"을 내 어림(깊이 12 언저리 2,500냥)에 대고 정했는데, 기준 계정의 장부(idb-test, 나라 셋 × 계정 30)로는 그
 * 관등의 출정 한 번이 비장 2,100~2,400 · 아장 3,800~4,500 · 부장 5,200~5,800냥이었다 — 현상금이 그 12~27%로, 정한
 * 3분의 1의 절반쯤이었다(녹림 대채·반란군은 29~39%로 맞았다). 3분의 1을 다 채우지는 않았다(700·1,300·1,800쯤이 된다):
 * 앞쪽 무리는 상점 장비만으로 15판을 다 이기는 쉬운 판이고, 급료·모집·진급을 뺀 순수입에 대면 지금도 후하다. 플레이한
 * 사람이 이 값(한 단계 낮게)을 골랐다.
 */
export interface RaidBand {
  readonly name: string;
  readonly chief: string;
  readonly note: string;
  readonly roster: readonly { readonly troop: string; readonly count: number }[];
  readonly level: number;
  readonly chiefLevel: number;
  readonly bounty: number;
}

export const RAID_BANDS: readonly RaidBand[] = [
  {
    name: '산적 떼',
    chief: '도적 두령',
    note: '굶주려 산에 든 무리. 장수 혼자서도 어렵지 않다.',
    roster: [
      { troop: 'bandit_thug', count: 6 },
      { troop: 'bandit_raider', count: 2 },
      { troop: 'bandit_archer', count: 2 },
    ],
    level: 1,
    chiefLevel: 1,
    bounty: 120,
  },
  {
    name: '도적 소굴',
    chief: '소굴의 두령',
    note: '고갯길을 막고 통행세를 걷는 무리. 산적이 섞여 있다.',
    roster: [
      { troop: 'bandit_thug', count: 10 },
      { troop: 'bandit_raider', count: 6 },
      { troop: 'bandit_archer', count: 4 },
    ],
    level: 6,
    chiefLevel: 8,
    bounty: 500,
  },
  {
    name: '큰 산채',
    chief: '산채의 두령',
    note: '탈영병이 든 산채. 창이 섞여 말 탄 장수에게 위험하다.',
    roster: [
      { troop: 'bandit_thug', count: 10 },
      { troop: 'bandit_raider', count: 8 },
      { troop: 'bandit_archer', count: 6 },
      { troop: 'bandit_deserter', count: 6 },
    ],
    level: 12,
    chiefLevel: 16,
    bounty: 1000,
  },
  {
    name: '마적단',
    chief: '마적 두목',
    note: '말을 탄 도적 떼. 달아나도 쫓아온다. 맨몸으로는 벅차다 — 갑옷과 말을 갖추고 가자.',
    roster: [
      { troop: 'bandit_raider', count: 12 },
      { troop: 'bandit_archer', count: 8 },
      { troop: 'bandit_deserter', count: 8 },
      { troop: 'bandit_rider', count: 6 },
    ],
    level: 18,
    chiefLevel: 24,
    bounty: 1600,
  },
  {
    name: '녹림 대채',
    chief: '녹림의 대두령',
    note: '고을 하나를 넘보는 큰 산채. 군대와 다르지 않다 — 좋은 장비 없이는 어렵다.',
    roster: [
      { troop: 'bandit_raider', count: 14 },
      { troop: 'bandit_archer', count: 10 },
      { troop: 'bandit_deserter', count: 12 },
      { troop: 'bandit_rider', count: 8 },
    ],
    level: 22,
    chiefLevel: 30,
    bounty: 2000,
  },
  {
    name: '반란군',
    chief: '반란의 수괴',
    note: '도적이 모여 깃발을 올렸다. 깊은 곳의 전리품을 두르지 않았으면 맞서기 어렵다.',
    roster: [
      { troop: 'bandit_raider', count: 16 },
      { troop: 'bandit_archer', count: 12 },
      { troop: 'bandit_deserter', count: 14 },
      { troop: 'bandit_rider', count: 10 },
    ],
    level: 27,
    chiefLevel: 36,
    bounty: 2600,
  },
];

/** 믿을 수 없는 무리 번호를 걸러 낸다(리플레이는 아무 값이나 실어 온다). 모르는 값은 가장 작은 무리다. */
export function raidBand(band: unknown): number {
  return typeof band === 'number' && Number.isInteger(band) && band >= 0 && band < RAID_BANDS.length ? band : 0;
}

export const TROOP_BY_ID: ReadonlyMap<string, TroopDef> = new Map(TROOPS.map((t) => [t.id, t]));

/**
 * 상성 배율(백분율). 공격자 가지 → 방어자 가지.
 *
 * 삼각형은 이렇다. 창이 기병을 세우고, 기병이 궁수를 덮치고, 궁수가 보병을 깎는다.
 * 이 세 변이 성립해야 편성에 고민이 생긴다. 한 병종만 모으는 게 정답이 되면
 * 수백 명이 뭉쳐 싸우는 그림 자체가 의미를 잃는다.
 */
const COUNTER: Record<Branch, Partial<Record<Branch, number>>> = {
  [Branch.Levy]: {},
  [Branch.Spear]: { [Branch.Horse]: 260 },
  [Branch.Blade]: { [Branch.Bow]: 130 },
  [Branch.Bow]: { [Branch.Horse]: 85 },
  [Branch.Horse]: { [Branch.Bow]: 175, [Branch.Levy]: 130, [Branch.Blade]: 120 },
};

export function counterPct(attacker: Branch, defender: Branch): number {
  const row = COUNTER[attacker];
  const v = row[defender];
  if (v !== undefined) return v;
  // 민병은 보병 취급을 받는다.
  if (attacker === Branch.Levy) return counterPct(Branch.Blade, defender);
  if (defender === Branch.Levy) return counterPct(attacker, Branch.Blade);
  return 100;
}

/**
 * 화살이 무시하는 방어 비율.
 *
 * 방어가 고정 차감이라 한 방이 약하고 간격이 긴 활은 갑옷 앞에서 아무것도 못 한다.
 * 계산해보니 정예 궁수가 개마무사를 눕히는 데 30초가 넘게 걸렸다. 그러면 활을
 * 데려갈 이유가 사라진다. 화살은 한 점에 힘을 모으므로 방어를 절반만 적용받는다.
 */
export const ARROW_ARMOR_PCT = 50;

/**
 * 궁수가 움직이는 적의 앞을 얼마나 내다보고 쏘는가(%) — 명중률이다. 100이면 화살이 닿을 때 적이 설 자리를 그대로 겨누고,
 * 0이면 쏘는 순간의 자리를 겨눈다(움직이는 적은 비껴 간다). 티어마다 바탕(`ARROW_LEAD_BY_TIER`), 레벨 5마다 1%p, 100까지.
 * 플레이한 사람과 정했다: "궁수에 명중률 개념과 피격 시점 타격 위치 예측 기능" — 주사위 대신 예측의 정확도로, 티어·
 * 레벨이 높을수록 잘 맞힌다(결정론 그대로). 낮은 티어 궁수는 여전히 달려드는 기병을 놓친다.
 *
 * 처음에는 35/55/75/90으로 두었다. 깊이 14~18 × 시드 40에서 전군 공격이 이김 144 → 130으로 떨어졌다 — 적 궁수가 돌진하는
 * 아군을 옆으로 달려도 맞힌다. 20/35/55/75로 낮춰도 133이라(대형은 162 → 160) 예측 자체의 값이고, 덜 흔드는 쪽을 골랐다.
 */
export const ARROW_LEAD_BY_TIER: readonly number[] = [0, 20, 35, 55, 75];

/**
 * **궁수는 표적을 나눠 겨눈다.** 표적을 고를 때 그 적을 이미 겨누는 제 편 궁수 한 명마다 `perAimerPx`만큼 더 먼 것으로
 * 친다 — 조금 더 먼 적이라도 겨누는 이가 적으면 그쪽을 고른다. 쥐고 있던 표적은 `stickPx`만큼 가깝게 쳐 준다(둘 사이를
 * 오가지 않게).
 *
 * 플레이한 사람이 청했다: "궁수들이 타겟을 골고루 잡았으면 좋겠어. 일점사를 너무 심하게 하지 않도록. 정 쏠곳이 없으면
 * 모르겠지만 조금 눈을돌려 약간만 이동하면 다른 타겟이 있다면 다른 타겟을 공격해줘." 전에는 저마다 가장 가까운 적을 골라,
 * 대형의 궁수 스물이 표적을 쥔 틱의 60~70%에서 **전원이 같은 적 하나**를 겨눴다(깊이 8·12·18, 시드 10). 화살은 맨 앞의
 * 적에 박히므로 한 명에게 스무 발이 가고 나머지는 맞지 않은 채 닿았다.
 *
 * 고를 수 있는 반경은 그대로다(사거리 + 30px — 그만큼은 걸어 나가 쏜다). 그 안에 적이 하나뿐이면 모두 그 적을 쏜다.
 */
export const BOW_SPREAD = { perAimerPx: 30, stickPx: 12 } as const;

/**
 * **근접 병사도 표적을 나눠 쫓는다.** 표적을 고를 때, 그 적을 이미 쫓는 제 편 근접 유닛이 `free`명을 넘으면 넘는 한 명마다
 * `perChaserPx`만큼 더 먼 것으로 친다 — 조금 더 먼 적이라도 덜 붐비면 그쪽으로 간다. 쥐고 있던 표적은 `stickPx`만큼
 * 가깝게 쳐 준다(둘 사이를 오가지 않게). 궁수의 `BOW_SPREAD`와 같은 꼴이다. 아군과 적군 모두에 건다.
 *
 * 플레이한 사람이 청했다: "근접캐릭터도 공격 타겟 분산 기능을 넣었으면 좋겠어. 너무 많은 사람들이 한 명을 쫓지 않도록
 * 말이야." 전에는 저마다 가장 가까운 적을 골라, 한 적을 쫓는 우리 근접 유닛이 평균 9~15명이었다(공격, 깊이 12~18,
 * `npm run chase`) — 닿지도 못하는 뒷사람이 줄을 서고, 포개져 서로 밀며 느려졌다.
 *
 * 궁수와 다른 점 셋.
 * - **`free`명까지는 값을 매기지 않는다**(고르는 사람까지 넣어 센다 — 다섯째부터 값이 붙는다). 화살은 한 명에게 스무 발이 가면 열여덟 발이 헛되지만, 칼은 여럿이 에워싸는 것이
 *   제대로 싸우는 것이다. 값은 같은 사람이 정했다: "무조건 분산하지 말고 … 네명까지 같은적 허용. 다섯 째부터 적용하고.
 *   적군 아군 다 적용해줘." 둘·셋으로도 재 봤다 — 몰림은 더 줄지만 공격 명령이 더 약해졌다(여럿이 한 명을 빨리
 *   쓰러뜨리는 것이 이 싸움에서는 실제로 이득이다).
 * - **지금 닿는 적은 붐벼도 값을 매기지 않는다**(`World.considerTarget`). 이미 붙어서 치는 것은 몰림이 아니라 에워싼
 *   것이다. 값은 걸어가야 하는 적에게만 매긴다.
 * - 장수·적장·동료(`freeOnLeader`)는 몸이 크고 오래 버텨 더 많이 붙어도 된다.
 *
 * 반경 안에 다른 적이 없으면 모두 그 적에게 간다 — 고를 것이 없으면 값은 뜻이 없다(잔병 구간이 그렇다). 모는 장수는
 * 플레이어가 고른 쪽을 친다.
 */
export const MELEE_SPREAD = { free: 4, freeOnLeader: 8, perChaserPx: 24, stickPx: 12 } as const;

/**
 * **곡사.** 서서 지키는 궁수(대형·대기)는 사거리 밖의 적을 비스듬히 위로 쏘아 맞힌다. 닿는 거리는 사거리의 `rangePct`%,
 * 화살은 느리고(`speedPx`/틱 — 곧은 화살은 15) 떨어질 자리가 `scatterPx` 안에서 흩어진다(시드 난수). 날아가는 동안에는
 * 아무도 맞지 않고 떨어진 자리에 선 적만 맞는다 — 앞줄을 넘어 뒤의 적에게 간다.
 *
 * 플레이한 사람이 청했다: "후방의 궁수들은 사거리부족으로 활 못쏘는것 같은데, 대각선 공중으로 쏘면 명줄률이 좀 더 떨어지지만
 * 좀 더 화살을 멀리 보낼 수 있는 기능도 추가해줘." 재 보니 그랬다: 싸움이 붙은 뒤 대형 궁수의 틱 22~41%가 사거리 밖이라
 * 표적이 없었고, 모자란 거리는 중앙값 120px쯤이었다(3티어 사거리 285px의 42%). 150%면 그 대부분에 닿는다.
 *
 * 곧게 쏠 수 있는 적이 있으면 곧게 쏜다 — 곡사는 그런 적이 없을 때만이다. 걸어 나가 싸우는 궁수(공격 명령, 적 궁수)는
 * 쓰지 않는다: 그들은 사거리까지 다가간다. 화살은 곡사도 한 발을 쓴다(`ARROWS_BY_TIER`).
 */
export const BOW_LOB = { rangePct: 150, scatterPx: 14, speedPx: 11 } as const;

export function arrowLeadPct(tier: number, level = 1): number {
  const base = ARROW_LEAD_BY_TIER[tier] ?? ARROW_LEAD_BY_TIER[ARROW_LEAD_BY_TIER.length - 1];
  return Math.min(100, base + Math.floor((clampLevel(level) - 1) / 5));
}

export function effectiveDef(defender: TroopDef, attacker: TroopDef): number {
  const raw =
    attacker.branch === Branch.Bow
      ? Math.floor((defender.def * ARROW_ARMOR_PCT) / 100)
      : defender.def;
  return Math.max(0, raw - attacker.pierce);
}

/**
 * 한 대 맞았을 때 들어가는 피해. 시뮬레이션의 판정과 같은 식이어야 한다.
 *
 * `shielded`: 친 쪽이 방패를 들었는가(`SHIELD_PENALTY`). 근접 대결은 화살이 없어 방패가 닳지 않으므로 벌점이 판 내내
 * 붙는다 — 처음에는 이 식이 벌점을 몰라 병종 표(`docs/troops.json`)의 창 대결이 방패 없는 몸의 수치였다(리뷰가 짚었다).
 */
export function hitDamage(a: TroopDef, b: TroopDef, shielded = false): number {
  const pct = counterPct(a.branch, b.branch);
  return Math.max(1, Math.floor((shieldedAtk(a.atk, shielded) * pct) / 100) - effectiveDef(b, a));
}

/**
 * 전투력 환산값.
 *
 * 손으로 적지 않고 수치에서 계산한다. 그래야 수치를 고치면 전투력이 따라 움직이고,
 * 표에 적힌 숫자와 실제 강함이 어긋나지 않는다.
 *
 *   초당 피해 × 실질 체력의 기하평균에 기동력과 사거리 보정을 곱한다.
 */
const DEF_REFERENCE_ATK = 12;

/**
 * 관통을 값으로 칠 때 기준이 되는 상대 방어.
 *
 * 관통은 상대 방어를 넘는 만큼은 버려진다. 표적의 방어가 평균 3쯤이므로 그
 * 위로는 값을 쳐 주지 않는다. 조의선인의 관통 5 중 뒤의 2는 개마무사를 만날
 * 때만 쓰이는 값이고, 전투력은 평균적인 상대를 기준으로 재는 숫자다.
 */
const PIERCE_REFERENCE_DEF = 3;

export function computePower(s: Stats, branch: Branch): number {
  const dps = ((s.atk + Math.min(s.pierce, PIERCE_REFERENCE_DEF)) * TICK_HZ) / s.cd;
  const soak = Math.max(1, DEF_REFERENCE_ATK - s.def);
  const ehp = (s.hp * DEF_REFERENCE_ATK) / soak;
  const mobility = 0.7 + (0.3 * s.speed) / 74;
  // 활은 맞기 전에 여러 번 쏜다. 사거리를 값어치로 환산한다.
  const reach = branch === Branch.Bow ? 1.55 + s.range / 900 : 1;
  return Math.round(Math.sqrt(dps * ehp) * mobility * reach);
}

export function troopPower(t: TroopDef): number {
  return computePower(t, t.branch);
}

/** 이 병종을 처음부터 다시 키우는 데 드는 돈. 잃었을 때의 실제 손실이다. */
export function rebuildCost(t: TroopDef): number {
  let total = 0;
  let tier = t.tier;
  const isHorse = t.branch === Branch.Horse;
  while (tier >= 2) {
    const horseLeg = isHorse && tier === 3;
    total += Math.round((TIER_COST[tier] * (horseLeg || (isHorse && tier === 4) ? HORSE_COST_PCT : 100)) / 100);
    tier--;
  }
  return total;
}

// ---------------------------------------------------------------------------
// 시뮬레이션이 쓰는 형태
// ---------------------------------------------------------------------------

/**
 * 가지의 숫자 코드. 전투 루프는 문자열을 만지지 않는다.
 * 문자열 비교는 느리기도 하지만, 무엇보다 배열 인덱스로 쓸 수 없다.
 */
export const BranchCode = {
  Levy: 0,
  Spear: 1,
  Blade: 2,
  Bow: 3,
  Horse: 4,
} as const;
export type BranchCode = (typeof BranchCode)[keyof typeof BranchCode];

const BRANCH_ORDER: readonly Branch[] = [
  Branch.Levy,
  Branch.Spear,
  Branch.Blade,
  Branch.Bow,
  Branch.Horse,
];

export function branchCode(b: Branch): BranchCode {
  return BRANCH_ORDER.indexOf(b) as BranchCode;
}

/**
 * 다친 병사가 물러나 추스르는 문턱과 빠르기. 가지 코드(`BranchCode`) 순서다.
 *
 * 체력이 최대의 `withdrawPct`% 밑으로 떨어지면 싸움을 피해 적에게서 물러나고, 적이 `WITHDRAW_CLEAR` 안에
 * 없는 곳에 가만히 서 있으면 초당 최대 체력의 `regenPctPerSec`%씩 추스른다. `RETURN_HP_PCT`까지 차면 다시
 * 싸우러 간다. 플레이한 사람이 청했다: "HP가 일정 비율 이하로 떨어지면 싸움을 회피하게 해줘. 적에게서 멀리
 * 떨어져서 가만히 서 있으면 hp를 일정 속도로 회복할 수 있어. 병종별로 도망치는 hp 비율과 회복 속도를 다르게."
 * 적도 같다(양쪽 모두로 정했다). 장수와 적장은 물러나지 않는다.
 *
 * - 민병: 싸울 뜻이 약하다. 일찍 빠지고 늦게 추스른다.
 * - 창: 줄을 지키는 몸이다. 가장 늦게까지 버틴다.
 * - 칼: 가운데.
 * - 활: 몸이 약하고 붙으면 쓸모가 없다. 가장 일찍 빠지고 가장 빨리 추스른다.
 * - 기병: 말과 사람이 값지다. 일찍 빠져 다시 달린다.
 */
export const WITHDRAW_BY_BRANCH: readonly { readonly withdrawPct: number; readonly regenPctPerSec: number }[] = [
  { withdrawPct: 40, regenPctPerSec: 3 }, // 민병
  { withdrawPct: 20, regenPctPerSec: 3 }, // 창
  { withdrawPct: 25, regenPctPerSec: 4 }, // 칼
  { withdrawPct: 40, regenPctPerSec: 5 }, // 활
  { withdrawPct: 30, regenPctPerSec: 4 }, // 기병
];
/** 물러난 병사가 다시 싸우러 가는 체력(최대의 %). 문턱과 벌려 두어 들락날락하지 않게 한다. */
export const RETURN_HP_PCT = 80;

/**
 * **화살통.** 궁수는 한 판에 티어만큼의 화살(`ARROWS_BY_TIER`, 0번은 비움)만 쏜다. 플레이한 사람이 청했다: "궁병도
 * 화살개수를 제한하자." 판 하나가 20~60초이고 장전이 1.2~1.6초라, 서서 쏘기만 하면 한 판에 15~50발을 쏜다 — 짧은 판에는
 * 닿지 않고 오래 끄는 판에서만 바닥난다. 도적 궁수(1티어)는 15발이다.
 *
 * 다 쏘면(`BOW_MELEE`): 이기는 판(제 편 병사의 전투력 합이 적 이상)이면 활을 놓고 근접으로 싸우고, 지는 판이면 제 진영
 * 가장자리로 물러나 빠져나간다. 그 순간 한 번 가르고 바꾸지 않는다 — 판세가 오갈 때마다 싸우다 도망치다를 되풀이하지 않게.
 */
export const ARROWS_BY_TIER: readonly number[] = [0, 15, 20, 25, 30];

export function arrowsFor(tier: number): number {
  return ARROWS_BY_TIER[tier] ?? ARROWS_BY_TIER[ARROWS_BY_TIER.length - 1];
}

/**
 * 궁수의 근접(`ARROWS_BY_TIER`). 활을 놓고 손에 든 것으로 친다 — 공격력은 `atkPct`%, 닿는 거리는 `rangePx`(민병과 같다),
 * 장전은 활과 같다. 화살이 남아 있어도 싸우는 적(궁수가 아닌, 무너지지 않은)이 `closePx` 안에 붙으면 근접으로 싸운다.
 * 플레이한 사람이 청했다: "화살 수가 남아있더라도 적이 근접하면 근접 공격을 하게 해줘." 예전에는 칼이 80px 안에 들면
 * 물러서며 쏘았다(벌점 70% 공격 · 1.5배 장전) — 도망치며 쏘는 궁수는 끝까지 잡히지 않아 가장 덜 죽는 병종이 됐다.
 * 근접의 화살 규칙(방어 절반, 포위 없음, 숲 가림)은 붙지 않는다 — 화살이 아니다.
 */
export const BOW_MELEE = { atkPct: 60, rangePx: WEAPONS[Weapon.Sword].reach, closePx: 32 } as const;

/**
 * **방패.** 방패를 든 병사는 앞쪽(바라보는 쪽 반원)에서 날아온 화살을 정해진 수만큼 막는다 — 막은 화살은 피해가 없고,
 * 수가 다하면 방패가 부서진다. 든 동안은 공격력이 `SHIELD_PENALTY.atkPct`%, 공격 간격이 `cdPct`%다. 부서지면 벌점도
 * 사라진다. 플레이한 사람이 청했다: "방패 개념을 도입해야겠어. 방패를 착용한 병사는 화살을 몇 발 막을 수 있지. 방패마다
 * 몇 대의 화살을 막을 수 있는지 정해져있어. 초과하면 방패가 파괴되지. 방패를 들고 있을 때는 공격속도와 공격력에 약간의
 * 패널티를 주자." 등 뒤로 날아든 화살은 막지 못한다 — 배후를 잡는 값(`FLANK_PCT`)과 같은 자리다.
 *
 * 병사는 **창**만 기본으로 든다(`SHIELD_BY_TIER`, 티어별 막는 수). 궁수 앞에서 화살을 받아 내며 궁수를 지키는 것이 창의
 * 자리라서다(병종 본능, `GUARD_RADIUS_PX`). 칼까지 들면 궁수가 보병을 깎는다는 상성(`COUNTER`)이 무너진다.
 */
export const SHIELD_BY_TIER: readonly number[] = [0, 0, 4, 6, 8];
export const SHIELD_PENALTY = { atkPct: 90, cdPct: 110 } as const;

/** 방패를 든 동안의 공격력. 판정(`World`)과 병종 표(`hitDamage`)가 이것 하나로 계산한다. */
export function shieldedAtk(atk: number, shielded: boolean): number {
  return shielded ? Math.floor((atk * SHIELD_PENALTY.atkPct) / 100) : atk;
}

/** 방패를 든 동안의 공격 간격(틱). */
export function shieldedCd(cd: number, shielded: boolean): number {
  return shielded ? Math.floor((cd * SHIELD_PENALTY.cdPct) / 100) : cd;
}

/** 병종 병사가 기본으로 드는 방패가 막는 화살 수. 없으면 0. */
export function shieldFor(branch: Branch, tier: number): number {
  if (branch !== Branch.Spear) return 0;
  return SHIELD_BY_TIER[tier] ?? SHIELD_BY_TIER[SHIELD_BY_TIER.length - 1];
}

/**
 * **병종 본능** — 명령 없이 병종마다 먼저 노리는 적. 상성표(`COUNTER`)가 "기병이 궁수를 덮친다"고 적어 두었지만 실제로는
 * 일어나지 않았다. 병사는 가장 가까운 적을 표적으로 잡는데, 궁수는 늘 앞줄 250~320px 뒤에 서 있어 기병이 앞줄부터 쳤다.
 * 측정해 보니(표준 혼성 부대, 장수가 산 판만) 깊이 18 대형에서 궁수 한 명이 3.08명을 베고 17%만 죽을 때 다른 병종은
 * 0.46~1.75명을 베고 50~96%가 죽었다. 플레이한 사람이 "궁수가 너무 좋은것 같은데"라고 짚었다.
 *
 * - 기병(`HUNT_RADIUS_PX`): 표적을 이 반경까지 넓게 보고, 적 궁수는 거리를 절반으로 쳐서 고른다 — 궁수가 두 배 멀리
 *   있어도 궁수에게 간다. 앞줄 너머라도 곧장 달려든다.
 * - 창(`GUARD_RADIUS_PX`): 제 편 궁수를 표적으로 잡은 적 기병을 이 반경 안에서 먼저 막아선다. 대형 명령이어도 진의
 *   반경 밖까지 나간다. 기병이 궁수를 노리면 창이 궁수를 지켜야 하는데, 모바일에서 "저 분대를 지켜라"를 짚기는 어렵다 —
 *   명령 대신 본능으로 두고, 플레이어는 궁수 곁에 창을 두는 편성으로 답한다.
 *
 * 병사에게만 건다. 장수·동료·두령은 플레이어의 조작이나 지휘를 따른다. 적도 같은 본능을 따른다.
 */
export const HUNT_RADIUS_PX = 330;
export const GUARD_RADIUS_PX = 260;

/** 병종 본능을 화면에 적는 한 줄(`HUNT_RADIUS_PX`). 없는 가지는 가장 가까운 적을 친다. */
export const BRANCH_INSTINCT: Partial<Record<Branch, string>> = {
  [Branch.Horse]: '본능: 적 궁수를 먼저 노린다. 앞줄 너머라도 달려든다.',
  [Branch.Spear]: '본능: 아군 궁수를 노리는 적 기병을 먼저 막아선다.',
};

/** 상성표를 평탄한 정수 배열로 굽는다. 전투 루프에서 한 번 읽고 끝낸다. */
export const COUNTER_TABLE: Int16Array = (() => {
  const t = new Int16Array(25);
  for (let a = 0; a < 5; a++) {
    for (let d = 0; d < 5; d++) {
      t[a * 5 + d] = counterPct(BRANCH_ORDER[a], BRANCH_ORDER[d]);
    }
  }
  return t;
})();

/** 전장에서의 역할. 장수와 동료와 적장은 병종 트리 밖에 있다. */
export const Role = {
  Soldier: 0,
  Leader: 1,
  Chief: 2,
  Companion: 3,
} as const;
export type Role = (typeof Role)[keyof typeof Role];

/**
 * 지휘 보정.
 *
 * 동료의 값어치는 자기가 베는 수가 아니라 휘하 분대를 얼마나 끌어올리는가에서 나온다.
 * 이렇게 두지 않으면 동료 몇 명만 데리고 다니는 것이 최적해가 되어, 수백 명이
 * 뭉쳐 싸우는 이 게임의 전제가 무너진다. 동료를 데려가는 이유가 병사를 더
 * 데려가기 위해서가 되어야 한다.
 */
export interface CaptainBonus {
  /** 분대원의 피해 배율(백분율). 100이면 보정 없음. */
  readonly atkPct: number;
  /** 분대원의 방어 가산. */
  readonly defBonus: number;
  /** 분대원의 사기 저항 가산. 높을수록 늦게 무너진다. */
  readonly gritBonus: number;
  /**
   * 그 부하장수가 **잘 부리는 병종**에만 더 붙는 몫(`MATE_TRAITS`). 분대의 그 병종 병사에게만 걸린다. 위의 세 값과 따로
   * 세고 상한(`CAPTAIN_CAP`)도 따로다 — 장비의 보정이 여기에 얹히지 않는다.
   */
  readonly favor?: Favor;
}

/** 잘 부리는 병종과 그 병종에만 붙는 몫: 공격 +%, 방어 +, 사기 +. 셋 가운데 하나만 0이 아니다. */
export interface Favor {
  readonly branch: BranchCode;
  readonly atkPct: number;
  readonly defBonus: number;
  readonly gritBonus: number;
}

/**
 * 분대 보정의 상한.
 *
 * 동료 하나가 주는 최대치는 공격 118%, 방어 +2, 사기 +60이다. 장비가 여기에
 * 그대로 더해지면 용봉문대도·금동장찰갑·금동관모·대장기를 겹친 장수가 사기
 * +140을 얹어, 동료가 누구냐는 선택이 장비 앞에서 무의미해진다. 상한을 두면
 * 장비는 동료를 대체하지 못하고 보탤 뿐이다.
 *
 * 사기 보정은 패주 문턱을 그만큼 내린다. 사기 120이면 무너지기까지 견디는
 * 충격이 두 배가 된다. 그 이상은 병사가 사실상 도망치지 않게 되고, 병력을
 * 언제 뺄 것인가라는 판단이 사라진다.
 */
export const CAPTAIN_CAP: CaptainBonus = { atkPct: 140, defBonus: 4, gritBonus: 120 };

/** 잘 부리는 병종에 붙는 몫의 상한(`favorAt`의 끝 값과 같다). */
export const FAVOR_CAP = { atkPct: 20, defBonus: 3, gritBonus: 60 } as const;

/** 보정 없음. 세 군데에 같은 리터럴을 적어 두면 한쪽만 고치게 된다. */
export const NO_CAPTAIN: CaptainBonus = { atkPct: 100, defBonus: 0, gritBonus: 0 };

export function clampCaptain(b: CaptainBonus): CaptainBonus {
  return {
    atkPct: Math.min(CAPTAIN_CAP.atkPct, b.atkPct),
    defBonus: Math.min(CAPTAIN_CAP.defBonus, b.defBonus),
    gritBonus: Math.min(CAPTAIN_CAP.gritBonus, b.gritBonus),
    ...(b.favor ? { favor: b.favor } : {}),
  };
}

/**
 * 출전 명부의 한 줄.
 *
 * 병사 하나가 전장에 들고 나가는 모든 것이 여기 담긴다. 장비와 개체 레벨이
 * 붙으면 stats가 병사마다 달라지므로, 리플레이는 시드가 아니라 이 목록을
 * 실어야 서버가 같은 전투를 재현할 수 있다.
 */
export interface CombatantSpec {
  /** 용사의 몸인가(`ELITE`). 그림과 스펙 표가 여느 병사와 가른다. */
  readonly elite?: boolean;
  readonly troop: string;
  readonly name: string;
  readonly branch: BranchCode;
  readonly role: Role;
  readonly culture: Culture;
  readonly tier: number;
  readonly power: number;
  readonly stats: Stats;
  /** 동료만 갖는다. 분대를 맡았을 때 휘하에 적용된다. */
  readonly captain?: CaptainBonus;
  /**
   * 레벨. 없으면 1이다.
   *
   * `World.specIndex`가 스펙을 묶는 열쇠에 들어간다. 병종·나라만으로 묶으면 같은
   * 병종의 레벨 3과 레벨 9가 먼저 세운 쪽 스펙 하나로 뭉쳐, 전장 밖(화면·격)에서
   * 읽는 값이 틀어진다.
   */
  readonly level?: number;
  /** 장수를 따르는 아군 병사인가(`retinueStats`). 같은 병종·레벨의 적병과 스펙이 다르다. */
  readonly retinue?: boolean;
  /** 드는 방패가 막는 화살 수(`SHIELD_BY_TIER`, 장수·동료는 착장의 방패). 없으면 0. */
  readonly shield?: number;
  /** 큰 방패인가(병사는 4티어, 장수·동료는 3티어 이상의 방패). 화면이 그림을 고른다 — 규칙에는 쓰이지 않는다. */
  readonly shieldBig?: boolean;
  /** 말에서 내린 기병·적장인가(전장 특성 `Trait.MountainPath`, `dismount`). 화면이 걷는 그림을 고른다. */
  readonly dismounted?: boolean;
  /**
   * 단련(백분율, `boostSpec`) — 깊이 `HEAD_DEPTH` 너머의 적. 체력·공격·격에 이미 들어 있다. `World.specIndex`의 열쇠에 든다:
   * 같은 병종·레벨이어도 단련이 다르면 다른 몸이다.
   */
  readonly boost?: number;
  /**
   * 장수·동료가 입은 것(장비 아이템 id). **화면 전용** — 림월드식 차림(`render/looks.ts`)이 층을 고른다. 규칙에는 쓰이지 않고
   * `World.specIndex`의 열쇠에도 들지 않는다(능력치는 이미 `stats`에 들어 있다).
   */
  readonly gear?: { readonly weapon?: string; readonly armor?: string; readonly helm?: string; readonly mount?: string };
}

/**
 * **용사** — 오래 살아남은 최고 병종의 병사를 장수가 가려 세운, 이름 있는 병사. 동료(부하 장수)는 아니지만 여느 병사보다
 * 단단하다. 플레이한 사람이 청했다(2026-10-05): "병사 최종 티어에 도달하고 일정 레벨을 달성하면, 부하 장수 급은 아니지만
 * 일반 병사보다 특별한 존재로 만들고 싶어." 정한 값(권장안을 그대로 받았다):
 * - 자격: 최고 티어(`tier`) + 레벨 `level` 이상. 장수가 돈을 내고 임명한다(`costMul` × 그 병종의 진급 값), 물릴 수 있다.
 * - 몸: 체력 `hpPct`%, 공격 `atkPct`%. **공격보다 체력**이다 — 용사의 뜻은 "잃기 아까운 병사가 덜 죽는다"이고, 공격을 크게
 *   올리면 여덟이 모였을 때 판이 달라져 관문 계단을 다시 맞춰야 한다. 병종마다 다른 몫은 뒤로 미뤘다.
 * - 이긴 판에서 쓰러지면 `woundedPct`%가 부상으로 돌아온다(여느 병사는 `WOUNDED_PCT` 30%). 진 판에서는 여느 병사와 같다 —
 *   "지면 용사를 잃는다"가 물러날 때를 재게 한다.
 * - 수: 관등마다(`ELITE_CAP`, army.ts), 많아야 `max`명. 명부가 이보다 많이 실어 와도 `max`까지만 용사의 몸을 준다.
 *
 * 임무(호위·궁수 보호)와 적의 용사는 다음 묶음이다.
 */
export const ELITE = { tier: 4, level: 20, hpPct: 120, atkPct: 110, woundedPct: 70, costMul: 2, max: 8 } as const;

/**
 * **용사는 장수를 호위한다**(2026-10-05). 플레이한 사람이 정했다: "궁수 보호 임무는 없애고, 용사는 무조건 장수를
 * 호위하게하자." 용사가 된 병사는 임무를 고르지 않는다 — 판에 서면 **제가 든 부대의 대장**(0번 부대는 장수, 부하 부대는
 * 그 부하장수) 곁에 선다. 누구를 지킬지는 이튿날 다시 정했다: "용사가 소속한 부대의 대장(장수, 혹은 부하장수)를 호위하면 돼". 그 사이(REPLAY 99)에는
 * 명부가 지킬 사람(`ward`: 장수가 기본, 고르면 부하장수)을 실어 왔고 용사 칸에 고르는 상자가 있었다.
 *
 * 처음에는 임무가 둘(호위·궁수 보호)이고 고르는 것이었다. 궁수 보호는 네 번 고쳤다 — 궁수 곁에 세우면 가장 단단한
 * 병사가 싸움에서 빠져 깊이 30의 이김이 78 → 54~60%로 내렸고, 나가 싸우다 불려 오게 하면 이김은 지켰지만 기병이
 * 궁수에게 닿을 때 용사는 늘 백수십 px 밖이었다(불린 용사는 접전 속을 제 걸음의 0.75배로 빠져나온다; QA가 다섯 빌드를
 * 120판씩 쟀다). 지키기와 싸우기 사이에 둘 다 되는 값이 없었다. 호위는 값이 없었다(이김 오차 안, 용사는 덜 쓰러진다).
 *
 * 호위하는 용사는 **분대의 명령(대형·공격·대기)과 경로를 따르지 않는다** — 지킬 사람 곁이 제 자리다. 그 사람 둘레에 온
 * 적만 치고, 그 사람을 표적으로 쥔 적을 먼저 친다. 후퇴와 장수가 쓰러진 뒤의 퇴각은 따른다. 지킬 부하장수가 판에 없거나
 * 쓰러지면 장수를 지키고, 장수까지 쓰러지면 여느 병사로 돌아간다. 다쳐서 물러나는 것, 무너지는 것은 여느 병사와 같다.
 *
 * - `radiusPx`: 지킬 사람에게서 이만큼 안에 든 적만 표적이다. 대형의 부대원이 지휘관 둘레를 보는 것(`FORMATION_RADIUS`)
 *   보다 좁다 — 호위가 싸움에 끌려 나가면 곁이 빈다.
 * - `leashPx`: 지킬 사람에게서 이보다 멀어지면 쥔 표적을 놓고 돌아온다(지금 칼이 닿는 적은 빼고).
 * - `nearPx`: 자리에서 이만큼 안이면 선다. `sprintPx`보다 멀면 뛰어서 따라붙는다(걸음의 한 배 반).
 * - 궁수인 용사는 곁에 서서 제 사거리로 쏜다. 지킬 사람에게서 `sprintPx`보다 멀어지면 쏘던 것을 놓고 따라붙는다 —
 *   처음에는 표적을 쥔 궁수에게 돌아가는 규칙이 없어, 장수가 달려 나가도 시작 자리에서 쏘기만 했다(장수까지 평균 401px).
 * - `stallTicks`·`restTicks`: 지킬 자리로 가는 길이 막힌 틱이 이만큼(2초) 쌓이면(제대로 걷거나 싸우는 틱에는 하나씩
 *   준다) 호위를 잠깐(5초) 내려놓고 분대의 명령을 따른다 — 분대의 걸음은 길을 찾는다(흐름장). 처음에는 곧장 가며 바위에
 *   미끄러지기만 해, 바위 곁에 낀 용사가 지킬 사람에게서 수백 px 떨어진 채 24초를 서 있었다(QA). 쉰 뒤 다시 간다.
 */
export const Duty = { None: 0, Escort: 1 } as const;
export type Duty = (typeof Duty)[keyof typeof Duty];
export const ELITE_DUTY = { radiusPx: 110, leashPx: 170, nearPx: 22, sprintPx: 90, stallTicks: 60, restTicks: 150 } as const;

/**
 * **적의 용사**(셋째 묶음, 2026-10-05). 플레이한 사람과 정했다: 용사는 "적에게도 있어." 깊은 곳의 적 부대에 섞여 나온다.
 * - 수: 깊이 `fromDepth`부터 `step`명, `every` 깊이마다 `step`명씩 더(`foeEliteCount`), **부대마다 `perSquad`명까지**
 *   (`foeElitePlan`). 토벌에는 없다. 117 전에는 깊이 10부터 하나, 다섯 깊이마다 하나씩, 판 전체에 여덟까지였다.
 * - 누구: **제 부대에서** 가장 센 **근접** 병사부터(티어·격 순, 같으면 색인 순). 궁수는 세우지 않는다 — 곁에서 몸으로
 *   막는 것은 근접의 일이다. 명부가 아니라 깊이와 부대에서 나오므로 서버가 같은 답을 낸다. 117 전에는 부대를 가리지
 *   않고 명부에서 가장 센 줄의 앞사람부터였다(부대가 없던 때의 규칙).
 * - 몸: 우리 용사와 같은 몫(체력 `ELITE.hpPct`%·공격 `ELITE.atkPct`%). **격은 `powerPct`%** — 처치 격이 곧 노획과
 *   전과라 "베면 전과를 더 준다"가 여기서 나온다.
 * - 임무: **제 부대의 대장**(총대장이나 부장)을 호위한다(`Duty.Escort`) — 우리 용사가 제 부대의 대장을 지키는 것과 같다
 *   (`World.bindDuties`). 그 대장이 쓰러지면 총대장을 지킨다. 117 전에는 모두 총대장을 지켰다.
 * - 원군·매복으로 뒤에 오는 적에는 없다(처음 선 편성에만).
 *
 * 「적의 부대」의 셋째 묶음(REPLAY 117). 플레이한 사람이 정했다(2026-10-07): "적도 부대별로 두 명의 용사를 등장시킬
 * 수도 있고." 수의 곡선(`step` 2)은 재지 않은 첫 값이다 — 깊이 10에 둘(부대마다 하나), 15에 넷, 20에 여섯, 25에 여덟,
 * 깊이 45부터 열여섯. **서는 수는 그 판의 부대 수에 막힌다**: 적장 결전(관문 20·30·50·60)은 세 부대라 여섯이고,
 * 깊이 30 너머의 여느 깊이는 다섯 부대라 열이다(머리 깊이 29의 명부) — 열여섯은 여덟 부대인 관문에서만 선다.
 */
export const FOE_ELITE = { fromDepth: 10, every: 5, step: 2, perSquad: 2, powerPct: 200 } as const;

/** 그 깊이의 판에 서는 적 용사의 수(부대 수로 막기 전 — `foeElitePlan`이 부대마다 `perSquad`명까지로 막는다). */
export function foeEliteCount(depth: number): number {
  const d = Math.floor(depth);
  if (!(d >= FOE_ELITE.fromDepth)) return 0;
  const n = FOE_ELITE.step * (1 + Math.floor((d - FOE_ELITE.fromDepth) / FOE_ELITE.every));
  return Math.min(FOE_ELITE.perSquad * FOE_SQUADS.maxSquads, n);
}

/**
 * 용사 `total`명을 `squads`부대에 나눈 수(부대 번호 순 — 0번이 총대장의 부대). 한 사람씩 돌아가며 준다: 0번부터 차례로
 * 하나씩, 한 바퀴를 돌면 다시 0번부터. 부대마다 `FOE_ELITE.perSquad`명까지다 — 남는 몫은 버린다.
 */
export function foeElitePlan(total: number, squads: number): number[] {
  const n = Math.max(1, Math.floor(squads));
  const out = new Array<number>(n).fill(0);
  let left = Math.max(0, Math.min(Math.floor(total), n * FOE_ELITE.perSquad));
  for (let k = 0; left > 0; k = (k + 1) % n, left--) out[k]++;
  return out;
}

/** 적 병사의 몸을 용사의 몸으로. */
export function foeEliteSpec(spec: CombatantSpec): CombatantSpec {
  return {
    ...spec,
    elite: true,
    power: Math.floor((spec.power * FOE_ELITE.powerPct) / 100),
    stats: { ...spec.stats, hp: Math.floor((spec.stats.hp * ELITE.hpPct) / 100), atk: Math.floor((spec.stats.atk * ELITE.atkPct) / 100) },
  };
}

/** 이 병종·레벨이 용사가 될 수 있는 몸인가. 명부는 클라이언트가 실어 오므로 판을 세울 때도 이것으로 거른다. */
export function eliteBody(troop: string, level: number): boolean {
  const t = TROOP_BY_ID.get(troop);
  return t !== undefined && t.tier >= ELITE.tier && clampLevel(level) >= ELITE.level;
}

export function specForTroop(id: string, level = 1, retinue = false, uid?: number, elite = false): CombatantSpec {
  const t = TROOP_BY_ID.get(id);
  if (!t) throw new Error(`없는 병종입니다: ${id}`);
  const lv = clampLevel(level);
  const base: Stats = {
    hp: t.hp,
    atk: t.atk,
    def: t.def,
    range: t.range,
    speed: t.speed,
    cd: t.cd,
    radius: t.radius,
    mass: t.mass,
    pierce: t.pierce,
    grit: t.grit,
  };
  // uid가 있으면 그 병사의 굴림(`soldierGrowth`), 없으면 평균.
  const raw = uid === undefined ? soldierStats(base, lv) : grown(base, soldierGrowth(uid, lv));
  const kept = retinue ? retinueStats(raw) : raw;
  // 용사의 몸(`ELITE`). 자격이 없는 병종·레벨에는 붙지 않는다.
  const isElite = elite && eliteBody(id, lv);
  const stats = isElite ? { ...kept, hp: Math.floor((kept.hp * ELITE.hpPct) / 100), atk: Math.floor((kept.atk * ELITE.atkPct) / 100) } : kept;
  return {
    troop: t.id,
    name: t.name,
    branch: branchCode(t.branch),
    role: Role.Soldier,
    culture: t.culture,
    tier: t.tier,
    // 격도 레벨을 탄다. 처치 격이 곧 노획과 전과라, 깊은 곳의 단련된 적을 베는
    // 값이 얕은 곳의 같은 병종과 같으면 깊이 들어갈 까닭이 줄어든다.
    power: computePower(stats, t.branch),
    stats,
    level: lv,
    retinue,
    ...(isElite ? { elite: true } : {}),
    shield: shieldFor(t.branch, t.tier),
    shieldBig: shieldFor(t.branch, t.tier) > 0 && t.tier >= 4,
  };
}

/**
 * 장수를 따르는 병사(정병)의 몸.
 *
 * 아군을 작게, 적을 크게 가기로 했다. 같은 병종·같은 레벨로 1 대 2를 붙이면 병사
 * 쪽이 녹는다 — 첫 출정 열둘이 깊이 1의 스물넷을 상대해 여덟을 잃었다. 장수가
 * 가려 뽑아 먹이고 입힌 사람들이라 같은 이름의 병종이라도 징발된 적병보다 단단하다.
 * 공격보다 체력을 더 올린다 — 적을 빨리 베는 것보다 살아 돌아오는 쪽이 이 게임의
 * 무게(영구 사망)에 맞는다.
 */ const RETINUE_HP_PCT = 200;
export const RETINUE_ATK_PCT = 125;

function retinueStats(s: Stats): Stats {
  return {
    ...s,
    hp: Math.floor((s.hp * RETINUE_HP_PCT) / 100),
    atk: Math.floor((s.atk * RETINUE_ATK_PCT) / 100),
  };
}

/**
 * 영웅. 장수와 적장은 진급 트리에 속하지 않는다.
 * 장수는 정면 반원을 휩쓸기 때문에 같은 공격력이라도 실제 화력이 훨씬 높다.
 */
function hero(
  troop: string,
  name: string,
  role: Role,
  culture: Culture,
  stats: Stats,
): CombatantSpec {
  return {
    troop,
    name,
    branch: BranchCode.Horse,
    role,
    culture,
    tier: 5,
    power: computePower(stats, Branch.Horse),
    stats,
  };
}

/**
 * 맨몸의 장수. 전장에 서는 몸이자, 장비 값어치를 재는 기준이다.
 *
 * 출처가 하나여야 한다. 예전에는 `equipment.ts`가 따로 쓴 기준 몸(체력 520,
 * 공격 18, 방어 4)으로 장비 값을 쟀다. 전장에 없는 몸이라 값이 어긋났다 —
 * 장비 대부분이 더하기로 붙으므로, 작은 몸에 얹으면 같은 물건이 더 크게
 * 보인다. 병기고 화면은 진짜 장수로 재고 검산 도구는 유령으로 재서, 같은
 * 물건의 값이 화면과 표에서 달랐다.
 */
// 얼려 둔다. `equipment.ts`의 `BARE_HERO`가 같은 객체를 가리키므로, 누가
// 기준 몸을 고치면 전장의 장수가 조용히 바뀐다. 결정론 코어의 상수다.
export const LEADER_BASE: Stats = Object.freeze({
  hp: 900,
  atk: 30,
  def: 9,
  range: HERO_REACH + 4,
  // **맨몸 장수는 걷는다.** 말을 타면 `RIDING_SPEED`(equipment.ts)가 더해진다.
  //
  // 예전에는 112였고 장수는 말이 없어도 늘 기병이었다. 플레이한 사람이 "말을 안
  // 채웠으면 보병으로 나와야 한다"고 했다. 84는 가장 빠른 보병(4티어 도검 78)보다
  // 조금 빠르다. 84 + 28 = 112라 말을 탄 장수의 수치는 예전 그대로다.
  speed: 84,
  cd: 16,
  // 영웅의 관통은 장비에서만 나온다. 맨몸에 주면 무기 선택이 흐려진다.
  pierce: 0,
  radius: 8,
  mass: 12,
  grit: 255,
});

/**
 * 맨몸 장수. 걷는 보병이다(도검 가지로 친다 — 칼을 든 장수다).
 *
 * 말을 태우는 것은 착장이 정한다(`scenario.ts`의 `dressLeader`). 기병이면 창에
 * 2.6배로 맞고 숲에서 느려지고 돌격이 붙는다. 걷는 장수는 그 셋이 다 없다.
 */
export function leaderSpec(culture: Culture, level = 1): CombatantSpec {
  const lv = clampLevel(level);
  const stats = heroStatsAt(LEADER_BASE, lv);
  const spec = hero('hero_leader', '장수', Role.Leader, culture, stats);
  const cmd = commandBonus(lv);
  return {
    ...spec,
    branch: BranchCode.Blade,
    power: computePower(stats, Branch.Blade),
    level: lv,
    // 레벨 1의 지휘는 보정이 없다. 비워 두면 맨몸 장수가 예전과 같은 길을 탄다.
    captain: cmd === NO_CAPTAIN ? undefined : cmd,
  };
}

/**
 * 장수의 레벨 성장.
 *
 * 예전에는 장수가 자라는 길이 지휘력(데려갈 인원) 하나였고 본인 능력치는 얼려
 * 두었다. 병사를 작게 가기로 하면서 인원이 성장축이 될 수 없게 됐다 — 이제 장수는
 * 몸도 자라고, 지휘는 휘하의 **질**을 올린다(`commandBonus`).
 *
 * 병사보다 느리게 는다. 장수는 레벨 1에서 이미 정예 열 명 몫이라, 같은 비율로
 * 자라면 혼자 판을 쓸어 부대를 데려갈 까닭이 사라진다.
 */
/** 장수 레벨 하나당 체력·공격(천분율). 옛 4%·3%의 3/5. */
export const HERO_LEVEL_HP_PERMILLE = 24;
export const HERO_LEVEL_ATK_PERMILLE = 18;
/** 방어는 이만큼의 레벨마다 1씩. 고정 차감이라 한 점이 크다. 옛 다섯 레벨의 5/3. */
export const HERO_LEVEL_DEF_EVERY = 8;

export function heroStatsAt(base: Stats, level: number): Stats {
  return leveled(base, level, HERO_LEVEL_HP_PERMILLE, HERO_LEVEL_ATK_PERMILLE, HERO_LEVEL_DEF_EVERY);
}

/**
 * 장수의 지휘. 레벨에서 나오고 **모든 분대**에 걸린다.
 *
 * 예전의 지휘력은 데려갈 수 있는 인원이었다. 분대 크기를 고정하면서 지휘는 부대를
 * 키우는 값이 아니라 부대를 **낫게** 하는 값이 됐다 — 레벨마다 타격 +0.6%, 사기 +2.4(내림),
 * 열세 레벨마다 방어 +1(레벨 99에서 옛 몫의 3/5씩 — 옛 눈금은 +1%·+4·여덟 레벨마다). 동료 보정·장비 보정과 더해지고, 합은 `CAPTAIN_CAP`에서
 * 멈춘다(`World.setCaptain`).
 *
 * 직할대만이 아니라 부하 부대에도 거는 것은, 장수가 이끄는 것은 부대 전체이기
 * 때문이다. 동료가 맡은 분대는 동료의 보정에 장수의 지휘가 얹힌다.
 */
export function commandBonus(level: number): CaptainBonus {
  const n = clampLevel(level) - 1;
  if (n === 0) return NO_CAPTAIN;
  return {
    atkPct: 100 + Math.floor((n * LEVEL_STEP_NUM) / LEVEL_STEP_DEN),
    defBonus: Math.floor(n / 13),
    gritBonus: Math.floor((4 * n * LEVEL_STEP_NUM) / LEVEL_STEP_DEN),
  };
}

/** 두 보정을 더한다. 백분율은 100을 기준으로 증가분끼리 더한다. 상한은 걸지 않는다. */
export function addCaptain(a: CaptainBonus, b: CaptainBonus): CaptainBonus {
  // 잘 부리는 병종(`favor`)은 더하지 않는다 — 한 분대에 하나다. 앞의 것이 남고, 없으면 뒤의 것(장수 곁의 부관이 그렇다:
  // 장수에게는 없으니 부관의 것이 내 부대에 걸린다).
  const favor = a.favor ?? b.favor;
  return {
    atkPct: a.atkPct + b.atkPct - 100,
    defBonus: a.defBonus + b.defBonus,
    gritBonus: a.gritBonus + b.gritBonus,
    ...(favor ? { favor } : {}),
  };
}

/**
 * **적의 부대**(`REPLAY_VERSION` 114 — docs/design.md 「적의 부대」의 둘째 묶음). 적도 부대마다 대장을 둔다: 총대장 하나
 * (지금까지의 적장)와 부장들, **최대 `maxSquads`부대**(우리는 넷). 플레이한 사람이 정했다(2026-10-07): "적도 장수를
 * 여럿두자. 아군과 동일한 시스템은데 아군은 4부대까지 등장할 수 있지만 적은 8부대까지 등장 할 수 있게하자."
 *
 * - `perSquad`: 병사 이만큼마다 한 부대(올림). 깊이 10의 31명이 둘, 15의 52명이 넷, 30 너머가 여덟.
 * - `subPct`: 부장의 몸은 같은 레벨 총대장의 이 %(체력·공격·격). 우리 부하장수가 장수보다 가벼운 것과 같다.
 * - `atkPct`·`gritBonus`: 대장이 살아 있는 동안 제 부대 병사에게 거는 지휘 보정. 작게 두었다 — 재지 않은 첫 값이다.
 * - `fallPct`: 부장이 쓰러지면 **그 부대의 병사만** 기개의 이 %를 잃는다. 총대장이 쓰러지면 예전대로 전군이 잃는다
 *   (`CHIEF_FALL_PCT`, world.ts). 60이었다(114) — 부장 하나에 부대가 통째로 무너져 달아났다: 관문의 판이 6~10초
 *   짧아지고, 우리 기병이 쓰러뜨리는 적 궁수가 86~88 → 60~68%로 줄었다(달아난 궁수는 세지 않는다 — idb-test).
 *   플레이한 사람이 정했다(2026-10-07): "부장 전사시 사기 손실 40프로로낮추자" — 총대장과 같은 값이다(115).
 *
 * 깊이 30까지는 부장의 격만큼 병사를 덜 세운다(`headRoster` — 예산 안에서 산다). 얹기만 하면 관문이 전부 어려워진다.
 * 토벌에는 부대가 없다(두령 하나). 적장 결전(관문 20)은 적장 셋이 세 부대의 대장이다 — 부장을 더 세우지 않는다.
 *
 * - `subFromDepth`: 이 깊이부터 부장이 선다. **첫 관문(깊이 5)까지는 총대장 하나뿐이다.** 114~115에서는 깊이 5의 적
 *   스무 명이 두 부대로 갈려 부장이 섰다 — 부장의 값만큼 병사가 줄었는데도(20 → 15명) 시작 군대로 첫 관문을 넘는
 *   판이 80% 아래로 내려가고 장수 생존이 줄었다(idb-test: 계단 44/57/61, 생존 81~87/90). 플레이한 사람이 정했다
 *   (2026-10-07): "깊이 5에서는 부장을 두지 말자". 깊이 1~4는 원래 한 부대였다(병사가 열여섯 아래).
 */
export const FOE_SQUADS = { maxSquads: 8, perSquad: 16, subPct: 75, atkPct: 105, gritBonus: 10, fallPct: 40, subFromDepth: 6 } as const;

/**
 * 그 부대의 대장이 **적장**인가(아니면 부장). 여느 판은 0번 부대의 대장(총대장) 하나뿐이고, **적장 결전은 0·1·2번
 * 부대의 대장 셋**이다(`WARLORDS` — 가운데 부대가 0번, 그 왼쪽 이웃이 1번, 오른쪽 이웃이 2번: `createBattle`). 전장의
 * 사기 규칙(`World.chiefFall`)과 화면의 직함·예고가 모두 이 한 줄을 본다 — 118 전에는 결전의 대장이 셋뿐이라 "결전이면
 * 모두 적장"으로 저마다 적었고, 결전에 부장을 세우자 그 가정이 한꺼번에 틀어졌다.
 */
export function foeHeadSquad(squad: number, warlords: boolean): boolean {
  return warlords ? squad >= 0 && squad < WARLORDS : squad === 0;
}

/** 병사 `soldiers`명의 적 군대가 나뉘는 부대 수(1~`maxSquads`). */
export function foeSquadCount(soldiers: number): number {
  const n = Math.floor(soldiers);
  if (!(n > 0)) return 1;
  return Math.max(1, Math.min(FOE_SQUADS.maxSquads, Math.ceil(n / FOE_SQUADS.perSquad)));
}

/** 적 대장이 제 부대에 거는 지휘 보정(`FOE_SQUADS`). 총대장과 부장이 같다. */
const FOE_CAPTAIN: CaptainBonus = Object.freeze({ atkPct: FOE_SQUADS.atkPct, defBonus: 0, gritBonus: FOE_SQUADS.gritBonus });

/** 적의 부장: 같은 레벨 총대장의 `FOE_SQUADS.subPct`%의 몸. 역할은 적장(`Role.Chief`)이다 — 총대장은 0번 부대의 대장이다. */
export function subChiefSpec(culture: Culture, level = 1): CombatantSpec {
  const c = chiefSpec(culture, level);
  const pct = FOE_SQUADS.subPct;
  return {
    ...c,
    troop: 'hero_subchief',
    name: '부장',
    power: Math.floor((c.power * pct) / 100),
    stats: { ...c.stats, hp: Math.floor((c.stats.hp * pct) / 100), atk: Math.floor((c.stats.atk * pct) / 100) },
  };
}

const CHIEF_BASE: Stats = Object.freeze({
  hp: 460,
  atk: 21,
  def: 6,
  range: HERO_REACH + 3,
  pierce: 0,
  speed: 104,
  cd: 20,
  radius: 8,
  mass: 10,
  grit: 255,
});

/** 적장. 레벨은 깊이에서 나온다(`foeChiefLevel`). 병사와 같은 비율로 자란다. */
export function chiefSpec(culture: Culture, level = 1): CombatantSpec {
  const lv = clampLevel(level);
  // 제 부대에 지휘 보정을 건다(`FOE_SQUADS` — 114부터). 토벌의 두령은 따로다(`banditChiefSpec`).
  return { ...hero('hero_chief', '적장', Role.Chief, culture, soldierStats(CHIEF_BASE, lv)), level: lv, captain: FOE_CAPTAIN };
}

/**
 * 동료.
 *
 * 이름을 가진 개체다. 전사하지 않고 쓰러질 뿐이며, 다음 출정 몇 번을 쉰다.
 * 전투력은 4티어 정예보다 조금 높은 수준에 그친다. 혼자 싸우라고 있는 것이 아니다.
 */
export interface CompanionDef {
  readonly id: string;
  readonly name: string;
  readonly title: string;
  readonly culture: Culture;
  readonly stats: Stats;
  readonly captain: CaptainBonus;
  readonly note: string;
  /** 이 사람만의 특성: 잘 부리는 병종과 레벨이 오를 때 지휘가 기우는 쪽(`MATE_TRAITS`). */
  readonly trait: MateTrait;
}

/**
 * **부하장수마다 다른 특성**(Todo11). 플레이한 사람: "부하장수들에 특성을 좀 다르게하자. 휘하에 걸리는 보정이 모두 동일한거
 * 같네. 레벨에 따라 보정향도 다르게하고 특색도 넣자. 병종별로 다른 보너스 수치를 주는것도 좋아보여. 열전에 맞는 특색을
 * 부여해줘." 그때까지는 유형 셋(용장·지장·덕장)이 전부였다 — 같은 유형이면 나라가 달라도 보정이 같았고, 레벨로 느는
 * 지휘는 아홉이 모두 같았다(`commandBonus`). 고유 기술(`MATE_SKILLS`)만 사람마다 달랐다.
 *
 * - `favor`: 잘 부리는 병종. 그 분대의 **그 병종 병사에게만** 더 붙는다. `kind`가 몫의 갈래다.
 *   - 'atk': 공격 +6%에서 시작해 여섯 레벨마다 +1%, +20%까지.
 *   - 'def': 방어 +1에서 시작해 레벨 30에 +2, 레벨 70에 +3.
 *   - 'grit': 사기 +20에서 시작해 두 레벨마다 +1, +60까지.
 * - `lean`: 레벨로 느는 지휘(`commandBonus`)가 기우는 쪽. 공격·사기의 몫을 10분의 몇으로(10이 예전 그대로), 방어는 몇
 *   레벨마다 +1인지(13이 예전 그대로). 용장은 공격으로, 지장은 사기로, 덕장은 방어로 기운다 — 레벨 1에서는 예전과 같고
 *   자랄수록 갈린다.
 *
 * 병종과 기우는 쪽은 열전(`ui/companion-lore.ts`)에서 가져왔다:
 * 온달 — 말 타고 활 쏘기, 사냥의 으뜸 → 기병. 을두지 — 성을 지키며 꾀를 냈다 → 궁수. 명림답부 — 들을 비우고 성을
 * 굳게 지켰다 → 창의 방어. 계백 — 결사대로 네 번 물리쳤다 → 도검. 성충 — 탄현과 기벌포를 막으라 → 창의 사기.
 * 흑치상지 — 흩어진 무리를 모아 성을 되찾았다 → 도검의 방어. 죽죽 — 성이 떨어질 때까지 싸웠다 → 창. 김유신 — 적진에
 * 뛰어들어 군을 일으켰다 → 기병(공격과 사기를 고르게). 알천 — 숨어든 적을 찾아 쳐 없앴다 → 궁수.
 */
export interface MateTrait {
  readonly favor: Branch;
  readonly kind: 'atk' | 'def' | 'grit';
  readonly lean: { readonly atk: number; readonly grit: number; readonly defEvery: number };
}

const LEAN_BRAVE = { atk: 14, grit: 6, defEvery: 13 } as const;
const LEAN_WISE = { atk: 6, grit: 14, defEvery: 13 } as const;
const LEAN_VIRTUOUS = { atk: 8, grit: 10, defEvery: 9 } as const;
const LEAN_EVEN = { atk: 10, grit: 10, defEvery: 13 } as const;

export const MATE_TRAITS: Readonly<Record<string, MateTrait>> = {
  go_ondal: { favor: Branch.Horse, kind: 'atk', lean: LEAN_BRAVE },
  go_euldudji: { favor: Branch.Bow, kind: 'atk', lean: LEAN_WISE },
  go_myeongnim: { favor: Branch.Spear, kind: 'def', lean: LEAN_VIRTUOUS },
  bj_gyebaek: { favor: Branch.Blade, kind: 'atk', lean: LEAN_BRAVE },
  bj_seongchung: { favor: Branch.Spear, kind: 'grit', lean: LEAN_WISE },
  bj_heukchi: { favor: Branch.Blade, kind: 'def', lean: LEAN_VIRTUOUS },
  sl_jukjuk: { favor: Branch.Spear, kind: 'atk', lean: LEAN_BRAVE },
  sl_yusin: { favor: Branch.Horse, kind: 'atk', lean: LEAN_EVEN },
  sl_alcheon: { favor: Branch.Bow, kind: 'atk', lean: LEAN_VIRTUOUS },
};

/** 그 레벨에서 잘 부리는 병종에 붙는 몫(`MateTrait.favor`). */
export function favorAt(trait: MateTrait, level: number): Favor {
  const n = clampLevel(level) - 1;
  const branch = branchCode(trait.favor);
  if (trait.kind === 'atk') return { branch, atkPct: Math.min(FAVOR_CAP.atkPct, 6 + Math.floor(n / 6)), defBonus: 0, gritBonus: 0 };
  if (trait.kind === 'def') return { branch, atkPct: 0, defBonus: Math.min(FAVOR_CAP.defBonus, n >= 69 ? 3 : n >= 29 ? 2 : 1), gritBonus: 0 };
  return { branch, atkPct: 0, defBonus: 0, gritBonus: Math.min(FAVOR_CAP.gritBonus, 20 + Math.floor(n / 2)) };
}

const COMPANION_BASE: Stats = {
  hp: 620,
  atk: 19,
  def: 9,
  range: HERO_REACH + 3,
  pierce: 0,
  // **맨몸 동료도 걷는다.** 말을 채우면 `RIDING_SPEED`(equipment.ts)가 더해진다.
  //
  // 예전에는 118이고 동료는 말이 없어도 늘 기병이었다. 장수가 말이 없으면 걷게 된
  // 뒤(`LEADER_BASE`) 동료만 공짜로 말을 타는 것이 어긋났다. 90 + 28 = 118이라 말을
  // 탄 동료의 수치는 예전 그대로다. 걷는 동료는 걷는 장수(84)보다 조금 빠르다 —
  // 예전에도 동료(118)가 장수(112)보다 빨랐다.
  speed: 90,
  cd: 20,
  radius: 8,
  mass: 10,
  grit: 255,
};

/**
 * 세 가지 장수 유형. 용장은 때리고, 지장은 버티게 하고, 덕장은 지킨다.
 * 셋 다 분대를 통해서만 값어치를 낸다.
 */
const ARCHETYPES = {
  brave: {
    title: '용장',
    delta: { atk: 5, hp: -40 } as Delta,
    captain: { atkPct: 118, defBonus: 0, gritBonus: 0 },
    note: '앞장서서 뚫는다. 휘하의 타격이 무거워진다.',
  },
  wise: {
    title: '지장',
    delta: { atk: -2 } as Delta,
    captain: { atkPct: 100, defBonus: 0, gritBonus: 60 },
    note: '대열이 늦게 무너진다. 불리한 싸움을 오래 끈다.',
  },
  virtuous: {
    title: '덕장',
    delta: { hp: 12, def: 2, atk: -1 } as Delta,
    captain: { atkPct: 100, defBonus: 2, gritBonus: 25 },
    note: '휘하가 덜 죽는다. 병사를 아끼는 편성에 맞는다.',
  },
} as const;

type ArchetypeKey = keyof typeof ARCHETYPES;

function companion(
  id: string,
  name: string,
  culture: Culture,
  kind: ArchetypeKey,
): CompanionDef {
  const a = ARCHETYPES[kind];
  const trait = MATE_TRAITS[id];
  if (!trait) throw new Error(`특성이 없는 부하장수다: ${id}`);
  return {
    id,
    name,
    title: a.title,
    culture,
    stats: applyDelta(COMPANION_BASE, a.delta),
    captain: a.captain,
    note: a.note,
    trait,
  };
}

export const COMPANIONS: readonly CompanionDef[] = [
  companion('go_ondal', '온달', Culture.Goguryeo, 'brave'),
  companion('go_euldudji', '을두지', Culture.Goguryeo, 'wise'),
  companion('go_myeongnim', '명림답부', Culture.Goguryeo, 'virtuous'),
  companion('bj_gyebaek', '계백', Culture.Baekje, 'brave'),
  companion('bj_seongchung', '성충', Culture.Baekje, 'wise'),
  companion('bj_heukchi', '흑치상지', Culture.Baekje, 'virtuous'),
  companion('sl_jukjuk', '죽죽', Culture.Silla, 'brave'),
  companion('sl_yusin', '김유신', Culture.Silla, 'wise'),
  companion('sl_alcheon', '알천', Culture.Silla, 'virtuous'),
];

export function companionsOf(culture: Culture): CompanionDef[] {
  return COMPANIONS.filter((c) => c.culture === culture);
}

/**
 * 동료가 합류하는 깊이.
 *
 * 처음부터 셋이 곁에 있으면 만나는 일이 사라진다. 깊이를 넘을 때마다 하나씩
 * 온다 — 위험을 치른 자리에서만 사람을 얻는다.
 *
 * 순서는 용장·지장·덕장이다. 첫 동료가 때리는 쪽인 것은, 그때가 부대가 제일
 * 얇아 한 판을 넘기는 것이 급한 시기이기 때문이다. 버티게 하고 지키는 쪽은
 * 데려갈 병사가 생긴 뒤에 값어치가 난다.
 *
 * **관문 5·10·15다**(`isGate`). 적 곡선을 깊이마다 20%로 펴고 깊이를 30까지 늘리며 옛 2·4·6에서
 * 옮겼다. `npm run stage`로 재면 첫 출정(2티어 열둘)이 관문 5를 넘고 10에서 막히며, 동료 하나(스물넷
 * Lv3)가 10을 넘고 15에서 막히고, 동료 둘(3티어 서른여섯)은 15를 겨우 넘는다. 사람 하나가 다음
 * 관문 하나를 여는 계단이다 — 옛 2·4·6이 "맨몸·장비·레벨"로 한 칸씩 열던 것과 같은 모양이다.
 */
export const COMPANION_DEPTHS: readonly number[] = [5, 10, 15];

/**
 * 이 깊이를 쓸었을 때 합류하는 동료. 없으면 null.
 *
 * `met`은 이미 만난 사람 수다. **깊이가 아니라 순서로 고른다.** 깊이를 색인으로
 * 쓰면 2를 건너뛰고 4를 쓴 사람에게 용장 없이 지장이 온다. 표가 약속한 순서는
 * 용장·지장·덕장이고, 첫 동료가 때리는 쪽인 데는 이유가 있다 — 그때가 부대가
 * 제일 얇아 한 판을 넘기는 것이 급한 시기다.
 *
 * 주사위를 굴리지 않는다. `(문화, 만난 수, 쓴 깊이)`의 순수 함수라 서버가 같은
 * 답을 낸다. 계정에 없는 동료를 실어 보낸 리플레이를 그 자리에서 거절할 수 있다.
 */
export function companionAtDepth(
  culture: Culture,
  depth: number,
  met: number,
): CompanionDef | null {
  if (met < 0 || met >= COMPANION_DEPTHS.length) return null;
  if (depth < COMPANION_DEPTHS[met]) return null;
  return companionsOf(culture)[met] ?? null;
}

/**
 * 동료의 레벨 — **장수와 같은 길**로 큰다(플레이한 사람이 청했다).
 *
 * - 경험치: 철수해 확정된 전과. 그 출정에 함께 섰던(쓰러지지 않은) 동료가 장수와 같은 몫을 받는다(`retreat`).
 * - 레벨 곡선: 장수와 같다(`heroLevelFor`, meta/army.ts).
 * - 몸: 장수와 같은 빠르기로 자란다(`heroStatsAt` — 체력 +2.4%·공격 +1.8%, 방어는 여덟 레벨마다 +1).
 * - 지휘: 장수의 지휘(`commandBonus`)와 같은 몫이 **제 분대에만** 붙는다 — 장수의 것은 모든 분대에 걸린다.
 *
 * 처음에는 레벨이 없고 **단계**(살아 돌아온 전투 0·5·12·25회, 넷이 끝)로 휘하 보정만 올랐다. 동료 하나가 혼자
 * 세지면 분대를 나눌 까닭이 준다고 몸은 얼려 두었는데, 장수는 이미 몸이 자라고 있었다. 같은 길로 맞추되 빠르기는
 * 장수의 것(병사보다 느리다)을 따른다. 레벨 10의 지휘(공격 +9%·사기 +36·방어 +1)가 옛 마지막 단계(+9%·+45·+1)와
 * 거의 같다 — 옛 계정의 단계는 레벨 1·4·7·10으로 옮긴다(`normalizeCompanions`).
 */
export function companionCaptain(def: Pick<CompanionDef, 'captain' | 'trait'>, level: number): CaptainBonus {
  // 레벨로 느는 지휘는 사람마다 기우는 쪽이 다르다(`MateTrait.lean`), 잘 부리는 병종의 몫도 레벨을 탄다(`favorAt`).
  const cmd = commandBonus(level);
  const n = clampLevel(level) - 1;
  const lean = def.trait.lean;
  const leaned: CaptainBonus = {
    atkPct: 100 + Math.floor(((cmd.atkPct - 100) * lean.atk) / 10),
    defBonus: Math.floor(n / lean.defEvery),
    gritBonus: Math.floor((cmd.gritBonus * lean.grit) / 10),
  };
  return { ...clampCaptain(addCaptain(def.captain, leaned)), favor: favorAt(def.trait, level) };
}

/**
 * 맨몸 동료. 걷는 칼 보병이다. 말을 채우면 `dressCompanion`(scenario.ts)이 기병으로
 * 올린다 — 장수(`leaderSpec`/`dressLeader`)와 같은 길이다.
 */
export function companionSpec(def: CompanionDef, level = 1): CombatantSpec {
  const stats = heroStatsAt(def.stats, clampLevel(level));
  return {
    troop: def.id,
    name: def.name,
    branch: BranchCode.Blade,
    role: Role.Companion,
    culture: def.culture,
    tier: 5,
    power: computePower(stats, Branch.Blade),
    stats,
    captain: companionCaptain(def, level),
  };
}

/** 검산 도구가 쓰는 표준 부대의 병사 레벨. 몇 판을 살아 돌아온 부대다(옛 눈금 5). */
export const STANDARD_LEVEL = 8;

/**
 * 문화별 기본 편성. 검산 도구가 쓰는 표준 부대다 — 네 분대를 꽉 채운 3티어 마흔여덟.
 *
 * 예전에는 88명이었다. 아군을 작게, 적을 크게 가기로 하면서 분대 하나가 열두 명으로
 * 고정됐다(`SQUAD_SIZE`, army.ts).
 */
export function standardRoster(
  culture: Culture,
  level = STANDARD_LEVEL,
): { troop: string; count: number; squad: number; level: number }[] {
  const slug = CULTURE_SLUG[culture];
  // 0번은 장수 직할대다. 1번부터는 동료가 맡는다.
  return [
    { troop: `${slug}_blade3`, count: 12, squad: 0, level },
    { troop: `${slug}_spear3`, count: 12, squad: 1, level },
    { troop: `${slug}_bow3`, count: 12, squad: 2, level },
    { troop: `${slug}_horse3`, count: 12, squad: 3, level },
  ];
}

/**
 * 머릿수가 느는 마지막 깊이. 그 뒤로는 머릿수를 그대로 두고 **레벨만** 오른다(113 전에는 단련 배율도 붙었다 — `depthRoster`).
 *
 * 적은 깊이마다 20%씩 세지는데 한 사람의 격은 4티어·레벨 20에서 100 안팎에서 멈춘다. 그 뒤로는 사람 수로만 늘어나고,
 * 전장에 설 수 있는 사람은 `MAX_UNITS`(1400)다. 깊이 30의 관문이 칠백여 명이고, 35면 추세만으로 천삼백을 넘어
 * `spawn`이 조용히 실패한다(적장이 먼저 사라진다). 그래서 예전에는 30이 끝이었다. 깊이를 무한으로 열며(Todo9) 30 뒤로는
 * 머릿수 대신 체력·공격을 올린다 — 레벨만으로는 모자란다(레벨 33 → 99가 깊이 넷 남짓의 몫이다).
 */
export const HEAD_DEPTH = 30;

/**
 * 깊이의 울타리. 깊이는 무한이지만(Todo9) 리플레이·주소창의 깊이를 정수로 막을 곳이 있어야 한다. 적의 레벨이 99에
 * 닿는 깊이 89 남짓부터는 더 세지지 않는다 — 거기가 끝이다(플레이한 사람과 정했다, 2026-10-07).
 */
export const MAX_DEPTH = 999;

/**
 * 단련 배율의 상한(백분율, ×2000). 처치한 적의 격을 모으는 `World.stats.killScore`와 체력이 Int32다 — 4티어·레벨 99의 격 400
 * 남짓 × 2000 × 적 1300명이 10억 남짓으로 2^31 아래에 든다. 이보다 크게 두면 처치 점수가 넘쳐 음수가 된다.
 */
export const BOOST_MAX_PCT = 200_000;

/**
 * 단련: 체력·공격·격을 `pct`%로 — 격이 √(체력×공격)에 비례하므로 셋을 같이 올린다. **113부터 적 명부는 단련을 싣지
 * 않는다**(`depthRoster`). 명부의 `boost` 칸과 이 함수는 남겨 두었다 — 리플레이 형식과 시험이 쓴다.
 */
export function boostSpec(spec: CombatantSpec, pct: number | undefined): CombatantSpec {
  if (pct === undefined || !(pct > 100)) return spec;
  const p = Math.min(Math.floor(pct), BOOST_MAX_PCT);
  return {
    ...spec,
    boost: p,
    power: Math.floor((spec.power * p) / 100),
    stats: { ...spec.stats, hp: Math.floor((spec.stats.hp * p) / 100), atk: Math.floor((spec.stats.atk * p) / 100) },
  };
}

/**
 * 관문. 이 깊이마다 적이 추세보다 한 번 크게 튄다(`GATE_PCT`). 다음 깊이는 추세로 돌아와
 * 한숨 돌린다. 동료는 관문을 넘을 때 합류한다(`COMPANION_DEPTHS`).
 */
export const GATE_EVERY = 5;
const GATE_PCT = 150;
// 매복(`Trait.Ambush`)이 붙은 관문(야습, 25)도 다른 관문과 같이 `GATE_PCT`로 튄다. 한때 1.25배로 낮췄었다
// (`GATE_AMBUSH_PCT`) — 매복은 명부 밖에서 처음 편성의 30%를 더 보내므로(`AMBUSH_PCT`) 1.5배에 얹으면 그 판만 1.95배가
// 되고, 그때는 관문 25가 벽이었다(넘는 데 10.5번). 성장 쪽을 올린 뒤로는(월급 `RANK_SALARY`, 깊은 곳의 경험치
// `battleXp`) 거꾸로 이 관문만 너무 쉬웠다: 1.25배면 2번(처음 도전 30/30), 1.5배로 되돌리면 4번이다(기준 5~6번).
// 낮춰 둔 위험을 제자리로 돌린 것이다.
/**
 * 깊이마다 적 전투력이 느는 비율(백분율).
 *
 * 120이었다. 기준 플레이어의 계정을 되풀이해 재니(`npm run career`, 계정 30개) 관문 사이가 너무 느렸다 — 관문 5·10·15·20을
 * 앞 관문에서 7·4·11·18.5번 만에 넘었다(기준 2~3·3~4·4~5·5~6). 병사를 넷에 하나꼴로 잃고 돌아오기를 되풀이해서다.
 * 117과 병사 규칙(부상 `WOUNDED_PCT`, 경험치 두 배 `battleXp`)을 함께 넣으면 3·3·5·5번이다. 곡선만 117이면 5·3·6·8번,
 * 115면 둘째·셋째가 기준보다 빨랐다(3.5번). 깊이 20에서 적 전투력은 옛 곡선의 62%다(1.17¹⁹ / 1.2¹⁹).
 * 관문 25 너머는 이 값으로도 벽이다 — 따로 맞춘다(docs/design.md "기준 플레이어의 계정을 돌려 본다").
 */
const DEPTH_GROWTH_PCT = 117;
/**
 * 깊이 `LATE_FROM` 너머의 기울기(백분율) — 머릿수가 느는 깊이 30까지의 목표 격에만 쓴다. 그 너머는 레벨만 오른다
 * (113 전에는 깊이 30 너머의 단련도 이 값을 따랐다).
 *
 * 깊이 20까지와 같은 1.17배로 두면 관문 25가 벽이었다 — 기준 플레이어의 계정 30개(`npm run career`) 가운데 13개만 넘고,
 * 넘는 데 35번이 걸렸다(기준은 관문마다 5~6번). 관문 20을 넘은 부대는 병사 53명·장수 Lv18인데 그 뒤로는 머릿수가
 * 관등(누적 전과)에 묶여 천천히 는다 — 적이 깊이마다 17%씩 세지는 것을 따라가지 못한다. 기울기만 바꿔 가며 쟀다
 * (관문 25 / 30까지의 출정 수): 1.14배 29 / —, 1.12배 22 / 13, 1.10배 17 / 18.5, 1.07배 11.5 / 19, 1.05배 10.5 / 6.5,
 * 1.03배 5.5 / 3. 1.05배에 매복 관문의 튐을 낮추면(`GATE_AMBUSH_PCT`) 7 / 5.5번이다. 처음 기준에는 "깊이 30 너머를
 * 1.1배 안팎으로"라고 적었는데, 재 보니 20 너머부터, 그리고 더 완만해야 했다.
 *
 * **112다(106이었다 — REPLAY 120).** 위의 값들은 분대 정원이 열둘이고 계단을 맨몸으로만 재던 때의 것이다. 그 뒤 정원이
 * 모든 관등 +2가 되고, 계단을 **장비와 함성을 얹은 판**으로 재게 되었다(`stairs --kit` — 플레이한 사람이 기준을 그
 * 줄에 대기로 했다, 2026-10-07). 그렇게 재니 6%에서는 깊이 20 너머가 벽이 아니었다: 관문 25의 부대가 관문 30을
 * 67~87%로 넘고(기준 20% 이하) 관문 30의 부대는 관문 35까지 93~100%로 넘었다. 기울기만 바꿔 가며 잰 값(30판, 장비 +
 * 함성, 고구려·백제·신라 — 관문 25의 부대가 관문 25 / 관문 30, 관문 30의 부대가 관문 30):
 *   6%  100·100·100 / 87·77·67 / 100·100·100      8%  100·100·97 / 43·43·30 / 97·100·93
 *   9%  100·100·97 / 30·30·7 / 100·97·77         10%  97·90·90 / 27·20·3 / 93·80·60
 *   12%  77·70·53 / 10·7·0 / 67·53·17            14%  33·33·17 / 0·0·0 / 13·27·13
 * 내 권장은 9~10%였다. 플레이한 사람이 12%로 정했다("기울기는 12프로로 하자") — **12%에서는 지금의 부대로는 제 관문도
 * 80%에 못 미친다**(관문 25: 77·70·53%, 관문 30: 67·53·17%). 적이 세지면 계정이 더 키워서 들어설 것으로 예상하지만
 * 확인한 것은 아니다 — 실제 이김과 관문까지의 출정 수는 `career`로 다시 잰다. 머릿수: 신라 적 깊이 25에 80 → 108명,
 * 30에 104 → 190명.
 */
const LATE_FROM = 20;
const LATE_GROWTH_PCT = 112;
/**
 * 모든 깊이의 적 목표 격에 곱하는 몫(백분율). **적이 횡대로 걸음을 맞춰 오게 된 뒤**(`World.lineMarch`) 넣었다 — 같은
 * 격의 적이어도 줄지어 차례로 닿을 때보다 한꺼번에 닿을 때 훨씬 세다. 같은 묶음(Todo11)에서 전장 세로를 32칸으로
 * 되돌리고 병사 체력을 120%로 올렸다(`SOLDIER_HP_PCT`) — 맞붙기 전에 궁수가 쏠 틈이 줄고, 체력은 수가 많은 쪽에 더
 * 붙는다. 셋을 넣고 적을 그대로 두면 관문에 들어서는 부대의 제 관문 이김(`npm run stairs`, 30판, 관문 5·10·15·20·25)이
 * 30·28·20·23·29 → 15·28·9·10·29였다.
 *
 * 기준 플레이어의 계정 90개(`npm run career`)로 맞췄다. 관문을 넘는 출정 수(앞 관문에서부터, 중앙값 — 관문 5·10·15·20·
 * 25·30)와 관문 30까지의 평균: 고치기 전 2.5·3·3·5·3·3(27.5번). 이 몫만 80%면 2·2·3·7.5·1.5·1(24.1), 85%면
 * 3·2·3·7·2·1(24.7), 90%면 3·2·3.5·8·2·1(28.9) — 어느 값에서도 관문 20(적장 결전)이 막히고 깊이 20 너머는 너무 쉽다.
 * 그래서 이 몫은 90으로 두고 둘을 따로 맞춘다: 적장 결전의 몫(80% — 119에서 없앴다, 아래)과 깊이 20 너머의 기울기
 * (`LATE_GROWTH_PCT` 105 → 106; 107이면 관문 25가 7번, 108이면 9번).
 */
const LINE_POWER_PCT = 90;
// **적장 결전의 몫은 없앴다**(`REPLAY_VERSION` 119). 118까지는 결전(관문 20·30, `Trait.Warlords`)의 적 목표 격에 80%를
// 곱했다(`WARLORDS_POWER_PCT`) — 적이 횡대로 오게 된 때, 적장 셋이 병사들과 한 줄로 닿는 이 판만 유난히 세서 관문 20을
// 넘는 데 8번이 들었기 때문이다. 그 뒤 분대 정원이 늘고(모든 관등 +2) 결전에도 부장이 서면서 이 깎기가 결전을 가장
// 쉬운 관문으로 만들었다: 관문 20에 들어선 부대는 30판을 다 이겼고, 관문 25의 부대가 관문 30을 그대로 넘었다(고구려
// 97%, 기준 20% 이하). 플레이한 사람이 정했다(2026-10-07): "결전 판의 적 병사 수 배율 자체를 없애자. 100%인거지. 다른
// 방식으로 조절하는게 좋겠어." 없앤 뒤의 값(`stairs` 30판, 고구려·백제·신라): 관문 20에 들어선 부대 87·77·50%, 관문 30에
// 들어선 부대 97·90·50%, 관문 25의 부대가 관문 30에서 40·33·13%. 신라가 낮다 — 신라의 병종 균형은 따로 본다.

/** 깊이 `k - 1`에서 `k`로 넘어갈 때 적 전투력이 느는 비율(백분율). */
function growthAt(k: number): number {
  return k > LATE_FROM ? LATE_GROWTH_PCT : DEPTH_GROWTH_PCT;
}
/** 깊이 1의 적 수. 이 수의 깊이 1 편성 격이 곡선의 출발점이다. */
const DEPTH1_COUNT = 12;

export function isGate(depth: number): boolean {
  return depth > 0 && depth % GATE_EVERY === 0;
}

/**
 * 깊이별 편성 비율. [깊이, 병종 비중] 기준점 사이를 정수로 잇는다. 병종 순서는
 * `DEPTH_TROOPS`와 같다(민병, 창2, 도검2, 궁2, 창3, 도검3, 궁3, 기병3, 창4, 도검4, 궁4, 기병4).
 */
const DEPTH_TROOPS = [
  'levy1', 'spear2', 'blade2', 'bow2',
  'spear3', 'blade3', 'bow3', 'horse3',
  'spear4', 'blade4', 'bow4', 'horse4',
] as const;
/** `DEPTH_TROOPS`에서 궁수(2·3·4티어)와 기병(3·4티어)의 자리. 전장 특성이 비중을 옮긴다(traits.ts). */
const BOW_SLOTS = [3, 6, 10] as const;
const HORSE_SLOTS = [7, 11] as const;
const DEPTH_MIX: readonly (readonly [number, readonly number[]])[] = [
  [1, [30, 25, 25, 20, 0, 0, 0, 0, 0, 0, 0, 0]],
  [6, [10, 25, 25, 15, 10, 10, 5, 0, 0, 0, 0, 0]],
  [12, [0, 12, 12, 8, 20, 20, 12, 12, 4, 0, 0, 0]],
  [20, [0, 0, 0, 0, 20, 20, 14, 16, 12, 8, 4, 6]],
  [30, [0, 0, 0, 0, 8, 8, 6, 8, 22, 18, 12, 18]],
];

function mixAt(d: number): number[] {
  const last = DEPTH_MIX[DEPTH_MIX.length - 1];
  if (d >= last[0]) return [...last[1]];
  let k = 0;
  while (DEPTH_MIX[k + 1][0] <= d) k++;
  const [d0, w0] = DEPTH_MIX[k];
  const [d1, w1] = DEPTH_MIX[k + 1];
  return w0.map((a, i) => a + Math.floor(((w1[i] - a) * (d - d0)) / (d1 - d0)));
}

/**
 * 적 병사의 레벨. 깊이 30에서 33(옛 눈금 20)이 되게 깊이마다 한 칸 남짓 오른다.
 *
 * 옛 식(`1 + floor((d−1)·2/3)`)을 `levelFromOld`로 옮기면 내림·반올림이 겹쳐 레벨이 두 칸씩 튀었다(1·1·3·4·4·6…) —
 * 깊이 곡선 시험의 위쪽을 풀어야 했다(리뷰가 짚었다). 곧장 새 눈금으로 적어 고르게 오른다. 끝은 같은 33이다.
 */
function enemyLevel(d: number): number {
  return clampLevel(1 + Math.floor(((d - 1) * 10) / 9));
}

/**
 * 깊이에 따른 적 편성.
 *
 * **머릿수가 아니라 전투력을 먼저 정한다.** 깊이 1은 열둘이고, 한 깊이마다 격의 합이 는다(`growthAt` — 깊이 20까지
 * 17%, 그 너머 5%. 처음에는 어디서나 20%였다). 관문(다섯 깊이마다)은 그 추세의 1.5배로 튀고 편성도 네 깊이
 * 앞의 것을 쓴다. 편성
 * 비율(`DEPTH_MIX`)과 레벨을 먼저 고르고, 목표 격을 한 사람 평균 격으로 나눠 머릿수를 낸다.
 *
 * 예전에는 병종마다 머릿수를 적고 깊이 배율을 곱했다(깊이 1에 24명 → 6에 178명). 그러면
 * 3티어가 처음 나오고 레벨이 오르는 깊이 2에서 격이 한 번에 2.4배(+140%)가 됐고, 깊이
 * 5쯤이면 끝이었다. 플레이한 사람이 "1에서 2로 너무 급하다, 깊이마다 20% 정도, 가끔 튀는
 * 구간, 깊이는 충분히"를 청했다. 머릿수를 격에서 거꾸로 내야 편성이 바뀌는 깊이에서도
 * 곡선이 튀지 않는다.
 *
 * 전부 정수 연산이다. 서버가 같은 명부를 만들어야 한다.
 */
export interface DepthEntry {
  troop: string;
  count: number;
  level: number;
  /** 단련(백분율, `boostSpec`). 113부터 `depthRoster`는 어느 깊이에도 싣지 않는다 — 형식과 손으로 세운 몸의 시험용으로 남겼다. */
  boost?: number;
}

export function depthRoster(culture: Culture, depth: number, traits: readonly Trait[] = []): DepthEntry[] {
  const d = Math.max(1, Math.min(MAX_DEPTH, Math.floor(depth)));
  if (d <= HEAD_DEPTH) return headRoster(culture, d, traits);
  // 깊이 `HEAD_DEPTH` 너머: 머릿수는 머리 깊이(관문이면 30, 아니면 29 — 관문의 튐을 남긴다)의 것, 같은 특성으로. **레벨만
  // 깊이대로 오른다** — 99까지(깊이 89 남짓), 그 너머는 더 세지지 않는다. 특성의 가감은 그대로다(인해 −15, 정예 +18 —
  // 깊이 90 너머: 일반 99·인해 84·정예 99).
  //
  // 113 전에는 모자란 몫을 **단련**(`boost` — 체력·공격·격에 곱하는 배율)으로 채워, 목표 격이 깊이마다 6%씩 곱으로 올랐다
  // (깊이 40에 149%, 50에 230%, 70에 588%). 플레이한 사람이 정했다(2026-10-07): "적 부대 성장은 지금처럼 인위적으로
  // 하지말고 뎁스가 증가함에 따라 레벨을 99까지 계속 올리자." 깊이 43에서 적장이 세게 느껴진다는 데서 나온 말이다 —
  // 레벨은 33인데 체력이 1.76배였다. 레벨의 몫은 한 칸에 일정량이라(직선) 깊은 곳이 전보다 훨씬 완만하다: 우리 쪽
  // 성장과 같은 규칙으로 맞선다. 부대와 지휘관·용사가 깊이를 따라 느는 것은 다음 묶음이다(적의 부대 — docs/design.md).
  const head = isGate(d) ? HEAD_DEPTH : HEAD_DEPTH - 1;
  const level = Math.min(LEVEL_MAX, depthLevel(d, traits));
  return headRoster(culture, head, traits).map((e) => ({ troop: e.troop, count: e.count, level }));
}

/**
 * 적장의 레벨. 깊이 `HEAD_DEPTH`까지는 예전 그대로(깊이가 곧 옛 눈금의 레벨 — 깊이 20에 33), 그 너머는 병사의 **기본
 * 레벨**과 같이 깊이를 따라 99까지 오른다(`enemyLevel`). 두 식은 깊이 30에서 같은 33이다. 특성(인해·정예)이 병사의
 * 레벨을 내리고 올려도 적장은 받지 않는다(깊이 43: 적장 47, 인해 병사 32, 정예 병사 65).
 *
 * 113 전에는 어디서나 `levelFromOld(깊이)`였다 — 옛 상한 20에서 잘려 깊이 20부터 33에 머물렀고, 깊이 30 너머에서는 레벨
 * 대신 단련 배율로만 세졌다(깊이 43: 레벨 33인데 체력 901 → 1,585). 플레이한 사람이 물었다: "적이 레벨 30까지만
 * 성장하는 이유는 뭐야?" 깊이 30까지를 그대로 둔 것은 거기까지의 관문 계단을 이 몸으로 맞췄기 때문이다.
 */
export function foeChiefLevel(depth: number): number {
  const d = Math.max(1, Math.min(MAX_DEPTH, Math.floor(depth)));
  return d <= HEAD_DEPTH ? levelFromOld(d) : Math.max(levelFromOld(d), enemyLevel(d));
}

/** 깊이와 특성이 정하는 적 레벨(인해는 내리고, 정예 소수는 올린다). */
function depthLevel(d: number, traits: readonly Trait[]): number {
  if (hasTrait(traits, Trait.Horde)) return Math.max(1, enemyLevel(d) - HORDE_LEVEL_DROP);
  if (hasTrait(traits, Trait.Elite)) return enemyLevel(d) + ELITE_LEVEL_UP;
  return enemyLevel(d);
}

/** `HEAD_DEPTH`까지의 명부(머릿수로 자란다)와 부대 수. */
function headPlan(culture: Culture, d: number, traits: readonly Trait[]): { roster: DepthEntry[]; squads: number } {
  const slug = CULTURE_SLUG[culture];
  const gate = isGate(d);
  const horde = hasTrait(traits, Trait.Horde);
  // 인해: 약한 대신 많다(`HORDE_LEVEL_DROP`). 레벨을 내리고 목표 격으로 머릿수를 다시 내면 저절로 는다.
  // 정예 소수는 그 거꾸로 — 레벨을 올려 머릿수가 준다(`ELITE_LEVEL_UP`).
  const level = depthLevel(d, traits);
  const mix = mixAt(gate ? d + 4 : d);
  // 궁수대: 궁수 비중 두 배. 모든 깊이의 편성(`DEPTH_MIX`)에 궁수가 있다.
  if (hasTrait(traits, Trait.ArcherCorps)) {
    for (const i of BOW_SLOTS) mix[i] *= 2;
  }
  // 기마대: 비중 합의 `CAVALRY_EXTRA_PCT`%를 기병에 얹는다. 깊이 14부터는 4티어 기병 — 한 사람의 격이 커서 같은
  // 전투력이면 머릿수는 오히려 준다(깊이 14: 55 → 41명). 적은 수의 무거운 돌격이 이 특성의 얼굴이다.
  if (hasTrait(traits, Trait.CavalryCorps)) {
    const extra = Math.floor((weightSum(mix) * CAVALRY_EXTRA_PCT) / 100);
    mix[d >= 14 ? HORSE_SLOTS[1] : HORSE_SLOTS[0]] += extra;
  }
  const ids = DEPTH_TROOPS.map((t) => `${slug}_${t}`);

  // 목표 격. 깊이 1 편성 열둘의 격에서 출발해 깊이마다 `growthAt`(깊이 20까지 `DEPTH_GROWTH_PCT`, 그 너머 `LATE_GROWTH_PCT`).
  const w1 = mixAt(1);
  const base = Math.round((DEPTH1_COUNT * weightedPower(ids, w1, 1)) / weightSum(w1));
  let target = base;
  for (let k = 2; k <= d; k++) target = Math.floor((target * growthAt(k)) / 100);
  // 횡대로 한꺼번에 닿는 적은 같은 격이어도 더 세다 — 그 몫을 뺀다(`LINE_POWER_PCT`).
  target = Math.floor((target * LINE_POWER_PCT) / 100);
  if (gate) target = Math.floor((target * GATE_PCT) / 100);
  if (horde) target = Math.floor((target * HORDE_POWER_PCT) / 100);
  if (hasTrait(traits, Trait.CavalryCorps)) target = Math.floor((target * CAVALRY_POWER_PCT) / 100);
  if (hasTrait(traits, Trait.Canyon)) target = Math.floor((target * CANYON_POWER_PCT) / 100);
  if (hasTrait(traits, Trait.Siege)) target = Math.floor((target * SIEGE_POWER_PCT) / 100);
  // 요철: 거센 적은 많고 지친 적은 적다(`tideAt`).
  if (hasTrait(traits, Trait.Surge)) target = Math.floor((target * SURGE_POWER_PCT) / 100);
  if (hasTrait(traits, Trait.Lull)) target = Math.floor((target * LULL_POWER_PCT) / 100);

  // 머릿수 = 목표 격 ÷ 한 사람 평균 격. 비중대로 나누고 나머지는 큰 몫부터(같으면 앞 병종).
  const wsum = weightSum(mix);
  const each = weightedPower(ids, mix, level);
  let total = Math.max(1, Math.round((target * wsum) / each));
  // **부장은 예산 안에서 산다**(`FOE_SQUADS`): 부대 수는 값을 치르기 **전**의 머릿수에서 정하고(`foeSquadCount`), 부대
  // 수에서 하나(총대장)를 뺀 만큼의 부장 격을 목표 격에서 덜어 머릿수를 다시 낸다. 전장은 그 부대 수를 여기서 받아 간다
  // (`foeSquadsAt`) — 줄어든 명부의 머릿수에서 다시 세지 않는다. 두 번 틀렸다: 줄어든 머릿수로 부대 수를 다시 세기를
  // 되풀이했더니 다섯 부대 값을 치르면 넷이 되고 넷 값을 치르면 다섯이 되는 틈에서 오갔고(깊이 21), 그 틈에서 부대
  // 정원까지만 세웠더니 값을 치르지 않은 병사가 빠졌다(깊이 5: 20 → 16명, 부장 없이).
  // 총대장은 예전부터 예산 밖이다(그 몸으로 계단을 맞췄다). **적장 결전**도 병사 수로 부대를 나눈다(118 — 「적의 부대」의
  // 넷째 묶음): 적장 셋(예산 밖)이 가운데 세 부대를 맡고 나머지 부대는 부장이 맡는다(값을 치른다). 117까지 결전은 적장
  // 셋의 세 부대뿐이라, 대장이 가장 많아야 할 판에 대장도 용사도 가장 적었다(관문 20·30에 셋·여섯, 관문 25에 일곱·여덟).
  const heads = hasTrait(traits, Trait.Warlords) ? WARLORDS : 1;
  // 첫 관문까지는 부장이 없다(`subFromDepth`) — 한 부대면 치를 값도 없어 머릿수가 그대로다.
  const squads = Math.max(heads, d < FOE_SQUADS.subFromDepth ? 1 : foeSquadCount(total));
  {
    const sub = subChiefSpec(culture, foeChiefLevel(d)).power;
    total = Math.max(squads, Math.round((Math.max(0, target - (squads - heads) * sub) * wsum) / each));
  }
  const counts = mix.map((w) => Math.floor((total * w) / wsum));
  let left = total - counts.reduce((a, b) => a + b, 0);
  const order = mix
    .map((w, i) => ({ i, rem: (total * w) % wsum }))
    .filter((e) => mix[e.i] > 0)
    .sort((a, b) => b.rem - a.rem || a.i - b.i);
  for (let k = 0; left > 0; k = (k + 1) % order.length, left--) counts[order[k].i]++;

  return {
    roster: ids.map((troop, i) => ({ troop, count: counts[i], level })).filter((e) => e.count > 0),
    squads: Math.min(squads, total),
  };
}

/** `HEAD_DEPTH`까지의 명부. */
function headRoster(culture: Culture, d: number, traits: readonly Trait[]): DepthEntry[] {
  return headPlan(culture, d, traits).roster;
}

/**
 * 그 깊이의 적 군대에 선 **부장들의 격의 합**(총대장은 뺀다 — 예전부터 명부 밖이다). 부장은 목표 격 안에서 사므로
 * (`headPlan`) 명부의 병사 격에 이것을 더해야 그 군대의 격이다. 화면의 "전투력"과 영토전의 자동 정산이 쓴다 — 처음에는
 * 병사만 세어, 부장을 세운 만큼 적이 약하게 적히고 영토전에서 약하게 잡혔다(리뷰·QA: 깊이 20의 6,812가 5,473).
 * 적장 결전은 적장 셋이 명부 밖이고 나머지 부대의 부장만 센다(117까지는 결전에 부장이 없어 0이었다).
 */
export function foeSubsPower(culture: Culture, depth: number, traits: readonly Trait[] = []): number {
  const heads = hasTrait(traits, Trait.Warlords) ? WARLORDS : 1;
  return Math.max(0, foeSquadsAt(culture, depth, traits) - heads) * subChiefSpec(culture, foeChiefLevel(depth)).power;
}

/**
 * 그 깊이의 적 군대가 나뉘는 부대 수(1~8, 적장 결전은 셋 이상). 명부(`depthRoster`)와 같은 입력에서 나온다 — 서버가 같은 판을
 * 세운다. 깊이 `HEAD_DEPTH` 너머는 명부와 같이 머리 깊이의 것이다. 토벌은 부르는 쪽이 거른다(부대가 없다).
 */
export function foeSquadsAt(culture: Culture, depth: number, traits: readonly Trait[] = []): number {
  const d = Math.max(1, Math.min(MAX_DEPTH, Math.floor(depth)));
  return headPlan(culture, d <= HEAD_DEPTH ? d : isGate(d) ? HEAD_DEPTH : HEAD_DEPTH - 1, traits).squads;
}

function weightSum(w: readonly number[]): number {
  return w.reduce((a, b) => a + b, 0);
}

/** 비중을 곱한 격의 합. 평균 격은 이것을 비중 합으로 나눈 값이다. */
function weightedPower(ids: readonly string[], w: readonly number[], level: number): number {
  let n = 0;
  for (let i = 0; i < ids.length; i++) if (w[i] > 0) n += w[i] * specForTroop(ids[i], level).power;
  return n;
}

